import {
  ApexAxisChartSeries,
  ApexNonAxisChartSeries,
  ApexAnnotations,
  ApexChart,
  ApexXAxis,
  ApexYAxis,
  ApexDataLabels,
  ApexGrid,
  ApexStroke,
  ApexTitleSubtitle,
  ApexLegend,
  ApexMarkers,
  ApexTooltip,
  ApexResponsive,
  ApexPlotOptions,
  ChartComponent,
} from 'ng-apexcharts';
import { Utils } from '../core/utils';
import { DataTakerStaffDataModel, GraphAnnotationModel, GraphAnnotationType, GraphDataModel, GraphGrouping, GraphSeriesModel } from '../core/api/models';
import { SelectItem } from 'primeng/api';
import { signal } from '@angular/core';
import {
  AnnotationLabelBackgroundOption,
  graphAnnotationDisplayLabel,
  resolveAnnotationLabelPositionValue,
} from './graph-annotation-display.utils';
import { PrintService } from '../shared/services/print.service';
import {
  AnnotationColorLegendGroup,
  AnnotationColorLegendItem,
} from './annotations/annotation-color-legend/annotation-color-legend.model';

export type {
  AnnotationColorLegendGroup,
  AnnotationColorLegendItem,
} from './annotations/annotation-color-legend/annotation-color-legend.model';

export type ChartOptions = {
  series: ApexAxisChartSeries | ApexNonAxisChartSeries;
  annotations: ApexAnnotations;
  chart: ApexChart;
  xaxis: ApexXAxis;
  yaxis: ApexYAxis | ApexYAxis[];
  dataLabels: ApexDataLabels;
  grid: ApexGrid;
  labels: string[];
  stroke: ApexStroke;
  title: ApexTitleSubtitle;
  legend: ApexLegend;
  markers: ApexMarkers;
  tooltip: ApexTooltip;
  responsive: ApexResponsive[];
  plotOptions: ApexPlotOptions;
  colors: string[];
};

export enum SeriesDataLabelOption {
  LineOnly = 'Line Only',
  Dots = 'Dots',
  DotsAndValues = 'Dots + Values',
  FirstAndLastValues = 'First + Last Values',
}

export enum TrendLineDataLabelOption {
  LineOnly = 'Line Only',
  Dots = 'Dots',
  DotsAndValues = 'Dots + Values',
  FirstAndLastValues = 'First + Last Values',
}

export enum AnnotationPresentationOption {
  OnGraph = 'On Graph',
  List = 'List',
}

export enum AnnotationLabelPosition {
  Begin = 'start',
  Middle = 'middle',
  End = 'end',
}

export {
  AnnotationLabelBackgroundOption,
  annotationSupportsLabelPosition,
  graphAnnotationDisplayLabel,
  resolveAnnotationLabelPositionValue,
} from './graph-annotation-display.utils';

export interface AnnotationListItem {
  annotation: GraphAnnotationModel;
  dateLabel: string;
  label: string;
}

export class GraphsBaseComponent {
  public chartOptions: Partial<ChartOptions> = {}
  
  // Store index mappings for tooltip access
  private seriesIndexMappings: Map<string, number[]> = new Map();
  private annotationDisplayByKey: Map<string, boolean> = new Map();
  private syncingAnnotationGroupCheckmarks = false;
  public chartsOptions: Partial<ChartOptions>[] = []; // Options for the synced charts
  public isGrouped = signal<boolean>(true); // Whether the graph is grouped or synced
  annotations = signal<GraphAnnotationModel[]>([]);
  displayAnnotations = signal<boolean>(true);
  displayAutomaticAnnotations = signal<boolean>(true);
  displayUserAnnotations = signal<boolean>(true);
  annotationPresentationOption = signal<AnnotationPresentationOption>(
    AnnotationPresentationOption.OnGraph
  );
  annotationLabelBackgroundOption = signal<AnnotationLabelBackgroundOption>(
    AnnotationLabelBackgroundOption.Transparent
  );
  declutterAnnotations = signal<boolean>(false);

  annotationPresentationOptions = [
    {
      label: AnnotationPresentationOption.OnGraph,
      value: AnnotationPresentationOption.OnGraph,
    },
    {
      label: AnnotationPresentationOption.List,
      value: AnnotationPresentationOption.List,
    },
  ];

  annotationLabelBackgroundOptions = [
    {
      label: AnnotationLabelBackgroundOption.Solid,
      value: AnnotationLabelBackgroundOption.Solid,
    },
    {
      label: AnnotationLabelBackgroundOption.Transparent,
      value: AnnotationLabelBackgroundOption.Transparent,
    },
  ];

  protected readonly defaultSeriesColors = [
    '#008FFB',
    '#00E396',
    '#FEB019',
    '#FF4560',
    '#775DD0',
    '#3F51B5',
    '#03A9F4',
    '#4CAF50',
  ];
  protected graphExportService: PrintService | null = null;

  /**
   * Optional handlers for toolbar download icons
   */
  handleDownloadPngClick(): void { /* no-op by default; children can override */ }
  handleDownloadPdfClick(): void { /* no-op by default; children can override */ }

  protected annotationDisplayKey(annotation: GraphAnnotationModel): string {
    if (!annotation.IsAutomatic && annotation.ID != null) {
      return `manual:${annotation.ID}`;
    }

    return [
      'automatic',
      annotation.Label ?? '',
      annotation.GraphAnnotationType ?? '',
      annotation.StartDate ?? '',
      annotation.EndDate ?? '',
      annotation.YValueStart ?? '',
      annotation.YValueEnd ?? '',
    ].join('|');
  }

  /**
   * ApexCharts removeAnnotation looks up a CSS class matching `id`. Pipe-delimited
   * display keys are invalid in querySelector and never match, so hide appears broken.
   */
  protected annotationApexId(annotation: GraphAnnotationModel): string {
    const safe = this.annotationDisplayKey(annotation)
      .replace(/[^a-zA-Z0-9_-]+/g, '-')
      .replace(/^-+|-+$/g, '');
    return `anno-${safe || 'item'}`;
  }

  isAnnotationDisplayed(annotation: GraphAnnotationModel): boolean {
    const key = this.annotationDisplayKey(annotation);
    return this.annotationDisplayByKey.has(key)
      ? this.annotationDisplayByKey.get(key)!
      : annotation.VisibleForUser ?? true;
  }

  isAnnotationDisplayedForPanel = (annotation: GraphAnnotationModel): boolean =>
    this.isAnnotationDisplayed(annotation);

  protected setAnnotationDisplayed(annotation: GraphAnnotationModel, isDisplayed: boolean): void {
    this.annotationDisplayByKey.set(this.annotationDisplayKey(annotation), isDisplayed);
  }

  protected setAnnotationsDisplayed(annotations: GraphAnnotationModel[], isDisplayed: boolean): void {
    annotations.forEach((annotation) => this.setAnnotationDisplayed(annotation, isDisplayed));
  }

  protected hasVisibleAnnotations(): boolean {
    return this.annotations().some((annotation) => this.isAnnotationDisplayed(annotation));
  }

  protected shouldRenderAnnotations(): boolean {
    return (
      this.annotationPresentationOption() === AnnotationPresentationOption.OnGraph &&
      this.hasVisibleAnnotations()
    );
  }

  shouldDisplayAnnotationList(): boolean {
    return (
      this.annotationPresentationOption() === AnnotationPresentationOption.List &&
      this.hasVisibleAnnotations()
    );
  }

  onAnnotationPresentationOptionChange(): void {
    this.refreshChartAnnotations();
  }

  onAnnotationLabelBackgroundOptionChange(): void {
    this.refreshChartAnnotations();
  }

  onDeclutterAnnotationsChange(): void {
    this.refreshChartAnnotations();
  }

  protected optionShowsMarkers(
    option: SeriesDataLabelOption | TrendLineDataLabelOption
  ): boolean {
    return option !== SeriesDataLabelOption.LineOnly && option !== TrendLineDataLabelOption.LineOnly;
  }

  protected optionShowsValues(
    option: SeriesDataLabelOption | TrendLineDataLabelOption
  ): boolean {
    return (
      option === SeriesDataLabelOption.DotsAndValues ||
      option === TrendLineDataLabelOption.DotsAndValues ||
      option === SeriesDataLabelOption.FirstAndLastValues ||
      option === TrendLineDataLabelOption.FirstAndLastValues
    );
  }

  protected optionShowsEndpointsOnly(
    option: SeriesDataLabelOption | TrendLineDataLabelOption
  ): boolean {
    return (
      option === SeriesDataLabelOption.FirstAndLastValues ||
      option === TrendLineDataLabelOption.FirstAndLastValues
    );
  }

  protected resolveSeriesColor(
    series: GraphSeriesModel | undefined | null,
    index: number
  ): string {
    if (series?.Color) {
      return series.Color;
    }
    return this.defaultSeriesColors[index % this.defaultSeriesColors.length];
  }

  protected resolveChartColors(
    series: GraphSeriesModel[] | undefined | null
  ): string[] {
    return (series ?? []).map((item, index) => this.resolveSeriesColor(item, index));
  }

  protected mapLineSeriesForChart(
    series: GraphSeriesModel,
    graphData: GraphDataModel,
    index: number
  ): { name: string; data: [number, number | null][]; color: string } {
    return {
      name: series.Name ?? 'No Name',
      data: this.processSeriesDataWithBreaks(series, graphData).data,
      color: this.resolveSeriesColor(series, index),
    };
  }

  /**
   * Returns indices of the first and last non-null value in each contiguous segment.
   * Null values (including series breaks) split the series into segments.
   */
  getEndpointIndicesFromSeriesData(data: any[] | undefined | null): number[] {
    if (!data || data.length === 0) {
      return [];
    }

    const endpoints = new Set<number>();
    let segmentStart: number | null = null;
    let segmentEnd: number | null = null;

    const flushSegment = () => {
      if (segmentStart != null && segmentEnd != null) {
        endpoints.add(segmentStart);
        endpoints.add(segmentEnd);
      }
      segmentStart = null;
      segmentEnd = null;
    };

    for (let i = 0; i < data.length; i++) {
      const point = data[i];
      const value = Array.isArray(point) ? point[1] : point;
      if (value === null || value === undefined || Number.isNaN(value)) {
        flushSegment();
        continue;
      }

      if (segmentStart == null) {
        segmentStart = i;
      }
      segmentEnd = i;
    }

    flushSegment();
    return Array.from(endpoints).sort((a, b) => a - b);
  }

  protected shouldShowValueAtIndex(
    option: SeriesDataLabelOption | TrendLineDataLabelOption,
    dataPointIndex: number,
    seriesData: any[] | undefined | null
  ): boolean {
    if (!this.optionShowsValues(option)) {
      return false;
    }
    if (!this.optionShowsEndpointsOnly(option)) {
      return true;
    }
    return this.getEndpointIndicesFromSeriesData(seriesData).includes(dataPointIndex);
  }

  protected buildLegendConfig(displayLegend: boolean): ApexLegend {
    if (!displayLegend) {
      return { show: false };
    }

    return {
      show: true,
      position: 'top',
      onItemClick: {
        toggleDataSeries: true,
      },
      onItemHover: {
        highlightDataSeries: true,
      },
      markers: {
        strokeWidth: 0,
        shape: 'square',
      },
    };
  }

  protected buildDiscreteEndpointMarkers(
    seriesIndex: number,
    seriesData: any[] | undefined | null,
    color: string,
    size: number = 3
  ): NonNullable<ApexMarkers['discrete']> {
    return this.getEndpointIndicesFromSeriesData(seriesData).map((dataPointIndex) => ({
      seriesIndex,
      dataPointIndex,
      size,
      fillColor: color,
      strokeColor: '#fff',
      shape: 'square' as const,
    }));
  }

  getVisibleAnnotationListItems(
    startDate?: Date | null,
    endDate?: Date | null
  ): AnnotationListItem[] {
    const startTime = startDate ? startDate.getTime() : null;
    const endTime = endDate ? endDate.getTime() : null;

    const items = this.annotations()
      .filter((annotation) => this.isAnnotationDisplayed(annotation))
      .filter((annotation) => this.isAnnotationInGraphPeriod(annotation, startTime, endTime))
      .map((annotation) => ({
        annotation,
        dateLabel: this.formatAnnotationListDateLabel(annotation),
        label: annotation.Label ?? '',
      }));

    return items.sort((a, b) => {
      const aTime = this.getAnnotationSortTime(a.annotation);
      const bTime = this.getAnnotationSortTime(b.annotation);
      if (aTime == null && bTime == null) {
        return (a.label || '').localeCompare(b.label || '');
      }
      if (aTime == null) {
        return 1;
      }
      if (bTime == null) {
        return -1;
      }
      if (aTime !== bTime) {
        return aTime - bTime;
      }
      return (a.label || '').localeCompare(b.label || '');
    });
  }

  protected isAnnotationInGraphPeriod(
    annotation: GraphAnnotationModel,
    startTime: number | null,
    endTime: number | null
  ): boolean {
    if (startTime == null || endTime == null) {
      return true;
    }

    const annotationStart = annotation.StartDate
      ? new Date(annotation.StartDate).getTime()
      : null;
    const annotationEnd = annotation.EndDate
      ? new Date(annotation.EndDate).getTime()
      : annotationStart;

    // Undated annotations (e.g. Average) apply to the whole graph period.
    if (annotationStart == null && annotationEnd == null) {
      return true;
    }

    const rangeStart = annotationStart ?? annotationEnd!;
    const rangeEnd = annotationEnd ?? annotationStart!;
    return rangeStart <= endTime && rangeEnd >= startTime;
  }

  protected getAnnotationSortTime(annotation: GraphAnnotationModel): number | null {
    if (annotation.StartDate) {
      return new Date(annotation.StartDate).getTime();
    }
    if (annotation.EndDate) {
      return new Date(annotation.EndDate).getTime();
    }
    return null;
  }

  formatAnnotationListDateLabel(annotation: GraphAnnotationModel): string {
    const startDate = annotation.StartDate ? new Date(annotation.StartDate) : null;
    const endDate = annotation.EndDate ? new Date(annotation.EndDate) : null;

    if (!startDate && !endDate) {
      return 'Graph period';
    }

    if (
      annotation.GraphAnnotationType === GraphAnnotationType.XAxisRange &&
      startDate &&
      endDate
    ) {
      return `${this.formatAnnotationDateTime(startDate)} - ${this.formatAnnotationDateTime(endDate)}`;
    }

    if (startDate && endDate && startDate.getTime() !== endDate.getTime()) {
      return `${this.formatAnnotationDateTime(startDate)} - ${this.formatAnnotationDateTime(endDate)}`;
    }

    return this.formatAnnotationDateTime(startDate ?? endDate!);
  }

  protected formatAnnotationDateTime(date: Date): string {
    return date.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  }

  protected areAllAnnotationsDisplayed(annotations: GraphAnnotationModel[]): boolean {
    return annotations.length > 0 && annotations.every((annotation) => this.isAnnotationDisplayed(annotation));
  }

  updateGraph(): void { /* Child graph components override this. */ }

  onAnnotationDisplayChange(annotation: GraphAnnotationModel, isDisplayed: boolean) {
    this.setAnnotationDisplayed(annotation, isDisplayed);
    this.updateAnnotationDisplayGroupCheckmarks();
    this.toggleChartAnnotation(annotation, isDisplayed);
  }

  protected patchLocalAnnotation(updated: GraphAnnotationModel): void {
    const key = this.annotationDisplayKey(updated);
    this.annotations.update((list) =>
      list.map((annotation) =>
        this.annotationDisplayKey(annotation) === key ? { ...annotation, ...updated } : annotation
      )
    );
  }

  protected patchAnnotationInGraphData(
    graphData: GraphDataModel | null,
    setGraphData: (value: GraphDataModel | null) => void,
    updated: GraphAnnotationModel
  ): void {
    if (!graphData?.Annotations?.length) {
      return;
    }

    const updatedKey = this.annotationDisplayKey(updated);
    const hasPersistedId = typeof updated.ID === 'number' && updated.ID > 0;
    setGraphData({
      ...graphData,
      Annotations: graphData.Annotations.map((annotation) => {
        if (hasPersistedId && annotation.ID === updated.ID) {
          return { ...annotation, ...updated };
        }
        if (!hasPersistedId && this.annotationDisplayKey(annotation) === updatedKey) {
          return { ...annotation, ...updated };
        }
        return annotation;
      }),
    });
  }

  protected applyAnnotationLabelPositionLocally(
    annotation: GraphAnnotationModel,
    labelPosition: 'start' | 'middle' | 'end'
  ): void {
    this.patchLocalAnnotation({ ...annotation, LabelPosition: labelPosition });
  }

  protected applyAnnotationLabelPositionChange(
    annotation: GraphAnnotationModel,
    labelPosition: 'start' | 'middle' | 'end',
    graphData: GraphDataModel | null,
    setGraphData: (value: GraphDataModel | null) => void,
    persistUpdatedAnnotation: (updated: GraphAnnotationModel) => void
  ): void {
    const updated: GraphAnnotationModel = { ...annotation, LabelPosition: labelPosition };
    this.applyAnnotationLabelPositionLocally(annotation, labelPosition);
    this.patchAnnotationInGraphData(graphData, setGraphData, updated);

    if (annotation.IsAutomatic || annotation.ID == null) {
      this.refreshChartAnnotations();
      return;
    }

    persistUpdatedAnnotation(updated);
  }

  protected isAnnotationIncludedForSeries(
    annotation: GraphAnnotationModel,
    series: GraphSeriesModel | null
  ): boolean {
    if (!series?.PatientTargetID) {
      return true;
    }

    return (
      !!annotation.PatientTargetIDs?.includes(series.PatientTargetID!) ||
      (annotation.PatientProgramIDs?.length ?? 0) > 0
    );
  }

  protected getAnnotationsShownOnGraph(
    graphData: GraphDataModel | null | undefined,
    series: GraphSeriesModel | null = null
  ): GraphAnnotationModel[] {
    if (!graphData?.Annotations?.length) {
      return [];
    }

    return graphData.Annotations.filter(
      (annotation) =>
        this.isAnnotationDisplayed(annotation) &&
        this.isAnnotationIncludedForSeries(annotation, series)
    );
  }

  protected resolveDeclutterAnnotationCandidates(
    graphData: GraphDataModel | null | undefined
  ): GraphAnnotationModel[] {
    return this.getAnnotationsShownOnGraph(graphData, null);
  }

  automaticAnnotations() {
    return this.annotations().filter((annotation) => annotation.IsAutomatic);
  }

  userAnnotations() {
    return this.annotations().filter((annotation) => !annotation.IsAutomatic);
  }

  onToggleAutomaticAnnotations(isDisplayed: boolean = this.displayAutomaticAnnotations()) {
    if (this.syncingAnnotationGroupCheckmarks) {
      return;
    }
    this.displayAutomaticAnnotations.set(isDisplayed);
    this.setAnnotationsDisplayed(this.automaticAnnotations(), isDisplayed);
    this.setAnnotationsDisplayed(
      (this.getGraphDataForAnnotations()?.Annotations ?? []).filter((annotation) => annotation.IsAutomatic),
      isDisplayed
    );
    this.updateAnnotationDisplayGroupCheckmarks();
    this.refreshChartAnnotations();
  }

  onToggleUserAnnotations(isDisplayed: boolean = this.displayUserAnnotations()) {
    if (this.syncingAnnotationGroupCheckmarks) {
      return;
    }
    this.displayUserAnnotations.set(isDisplayed);
    this.setAnnotationsDisplayed(this.userAnnotations(), isDisplayed);
    this.setAnnotationsDisplayed(
      (this.getGraphDataForAnnotations()?.Annotations ?? []).filter((annotation) => !annotation.IsAutomatic),
      isDisplayed
    );
    this.updateAnnotationDisplayGroupCheckmarks();
    this.refreshChartAnnotations();
  }

  onDisplayAnnotationsChange(isDisplayed: boolean = this.displayAnnotations()) {
    if (this.syncingAnnotationGroupCheckmarks) {
      return;
    }
    this.displayAnnotations.set(isDisplayed);
    this.setAnnotationsDisplayed(this.annotations(), isDisplayed);
    this.setAnnotationsDisplayed(this.getGraphDataForAnnotations()?.Annotations ?? [], isDisplayed);
    this.displayUserAnnotations.set(isDisplayed);
    this.displayAutomaticAnnotations.set(isDisplayed);
    this.refreshChartAnnotations();
  }

  protected updateAnnotationDisplayGroupCheckmarks() {
    this.syncingAnnotationGroupCheckmarks = true;
    try {
      this.displayAutomaticAnnotations.set(this.areAllAnnotationsDisplayed(this.automaticAnnotations()));
      this.displayUserAnnotations.set(this.areAllAnnotationsDisplayed(this.userAnnotations()));
      this.displayAnnotations.set(this.areAllAnnotationsDisplayed(this.annotations()));
    } finally {
      this.syncingAnnotationGroupCheckmarks = false;
    }
  }

  annotationLabel(annotation: GraphAnnotationModel): string {
    return graphAnnotationDisplayLabel(annotation);
  }

  // Shared chart configurations
  protected readonly sharedChartConfig = {
    zoom: {
      enabled: true,
      type: 'x' as const,
      autoScaleYaxis: false,
      allowMouseWheelZoom: false,
      zoomedArea: {
        fill: {
          color: '#90CAF9',
          opacity: 0.4,
        },
        stroke: {
          color: '#0D47A1',
          opacity: 0.4,
          width: 1,
        },
      },
    },
    toolbar: {
      show: true,
      offsetX: 0,
      offsetY: 0,
      tools: {
        download: true,
        selection: true,
        zoom: true,
        zoomin: true,
        zoomout: true,
        pan: true,
        reset: true,
        customIcons: [
          {
            icon: '<span style="padding:0 6px; display:inline-flex; align-items:center"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg></span>',
            index: 5,
            class: 'custom-icon-download-png',
            title: 'Download PNG',
            click: (chart: any, options: any, e: any) => {
              this.handleDownloadPngClick();
            }
          },
          {
            icon: '<span style="padding:0 6px; display:inline-flex; align-items:center"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6,9 6,2 18,2 18,9"></polyline><path d="M6,18H4a2,2,0,0,1-2-2V11a2,2,0,0,1,2-2H20a2,2,0,0,1,2,2v5a2,2,0,0,1-2,2H18"></path><rect x="6" y="14" width="12" height="8"></rect></svg></span>',
            index: 5,
            class: 'custom-icon-print-pdf',
            title: 'Print PDF',
            click: (chart: any, options: any, e: any) => {
              this.handleDownloadPdfClick();
            }
          }
        ],
      },
      export: {
        csv: {
          filename: undefined,
          columnDelimiter: ',',
          headerCategory: 'category',
          headerValue: 'value',
          dateFormatter(timestamp?: number) {
            return new Date(timestamp!).toDateString();
          },
        },
        svg: {
          filename: undefined,
        },
        png: {
          filename: undefined,
        },
      },
      autoSelected: 'zoom' as const,
    },
  };

  // Date Options
  dateOptions: SelectItem[] = [
    { value: 0, label: 'Custom' },
    { value: Utils.dateInPastBy(1), label: '1 day' },
    { value: Utils.dateInPastBy(5), label: '5 days' },
    { value: Utils.dateInPastBy(14), label: '14 days' },
    {
      value: Utils.dateInPastBy(Utils.daysToBeginningOfMonth()),
      label: 'Month to Date',
    },
    { value: Utils.dateInPastBy(30), label: '1 month' },
    { value: Utils.dateInPastBy(90), label: '3 months' },
    { value: Utils.dateInPastBy(180), label: '6 months' },
    {
      value: Utils.dateInPastBy(Utils.daysToBeginningOfYear()),
      label: 'Year to Date',
    },
  ];
  dateOption: SelectItem = this.dateOptions[6];

  // Grouping Options
  groupingOptionsIncludingStaffOptionAndByTrial: any[] = [
    { value: GraphGrouping.BySession, text: 'By Session' },
    { value: GraphGrouping.ByStaffMember, text: 'By Staff Member' },
    { value: GraphGrouping.ByTrial, text: 'By Trial' },
    { value: GraphGrouping.Daily, text: 'Daily' },
    { value: GraphGrouping.Weekly, text: 'Weekly' },
    { value: GraphGrouping.Monthly, text: 'Monthly' },
    { value: GraphGrouping.Yearly, text: 'Yearly' },
  ];

  groupingOptionsIncludingStaffOption: any[] = [
    { value: GraphGrouping.BySession, text: 'By Session' },
    { value: GraphGrouping.ByStaffMember, text: 'By Staff Member' },
    { value: GraphGrouping.Daily, text: 'Daily' },
    { value: GraphGrouping.Weekly, text: 'Weekly' },
    { value: GraphGrouping.Monthly, text: 'Monthly' },
    { value: GraphGrouping.Yearly, text: 'Yearly' },
  ];

  groupingOptionsExcludingStaffOption: any[] = [
    { value: GraphGrouping.BySession, text: 'By Session' },
    { value: GraphGrouping.Daily, text: 'Daily' },
    { value: GraphGrouping.Weekly, text: 'Weekly' },
    { value: GraphGrouping.Monthly, text: 'Monthly' },
    { value: GraphGrouping.Yearly, text: 'Yearly' },
  ];

  groupingOptionsExcludingStaffOptionWithByTrial: any[] = [
    { value: GraphGrouping.BySession, text: 'By Session' },
    { value: GraphGrouping.ByTrial, text: 'By Trial' },
    { value: GraphGrouping.Daily, text: 'Daily' },
    { value: GraphGrouping.Weekly, text: 'Weekly' },
    { value: GraphGrouping.Monthly, text: 'Monthly' },
    { value: GraphGrouping.Yearly, text: 'Yearly' },
  ];

  groupingOptions: any[] = this.groupingOptionsIncludingStaffOption;
  groupingOption: GraphGrouping = this.groupingOptions[2].value; // Daily

  loadDefaultGraphOptions() {
    this.chartOptions = {
      legend: {
        position: 'top',
        onItemClick: {
          toggleDataSeries: true,
        },
        onItemHover: {
          highlightDataSeries: true,
        },
      },
      chart: {
        height: 300,
        type: 'line',
        ...this.sharedChartConfig,
        events: this.createAnnotationReorderEvent(),
      },
      dataLabels: {
        enabled: false,
      },
      stroke: {
        curve: 'straight',
      },
      grid: {
        padding: {
          right: 30,
          left: 20,
        },
      },
      markers: this.getMarkersConfig(),
      tooltip: {
        x: {
          format: 'MMM dd yyyy',
        },
        y: {
          formatter: (value: any) => {
            if (value === null) {
              return '';
            }
            return value + ' %';
          },
        },
      },
      title: {
        text: 'Chart',
      },
      xaxis: {
        type: 'datetime',
        title: {
          text: 'Dates',
        },
        labels: {
          datetimeUTC: false,
        },
      },
      yaxis: {
        title: {
          text: 'Values',
        },
        labels: {
          formatter: (value: number) => {
            // If it's a whole number, don't show decimal places
            if (Number.isInteger(value)) {
              return value.toString();
            }
            // If it's a decimal, round to 2 decimal places
            return value.toFixed(2);
          }
        }
      },
    };
  }

  enabledOnSeriesForDataPointDisplayOption(displayDataPointOption: SeriesDataLabelOption): number[] {
    switch (displayDataPointOption) {
      case SeriesDataLabelOption.LineOnly:
        return [];
      case SeriesDataLabelOption.Dots:
      case SeriesDataLabelOption.DotsAndValues:
      case SeriesDataLabelOption.FirstAndLastValues:
        return [0];
      default:
        return [0];
    }
  }

  getDataLabelsConfig(displayDataPointOption: SeriesDataLabelOption = SeriesDataLabelOption.DotsAndValues): any {
    return {
      enabled: true,
      enabledOnSeries: this.enabledOnSeriesForDataPointDisplayOption(displayDataPointOption),
      formatter: (value: any, { seriesIndex, dataPointIndex, w }: any) => {
        const seriesData = w?.config?.series?.[seriesIndex]?.data;
        if (!this.shouldShowValueAtIndex(displayDataPointOption, dataPointIndex, seriesData)) {
          return '';
        }
        return value || value === 0 ? value : '';
      },
    };
  }

  getMarkersConfig(displayDataPointOption: SeriesDataLabelOption = SeriesDataLabelOption.DotsAndValues): ApexMarkers {
    const showsMarkers = this.optionShowsMarkers(displayDataPointOption);
    const endpointsOnly = this.optionShowsEndpointsOnly(displayDataPointOption);
    const seriesSize = showsMarkers && !endpointsOnly ? 3 : 0;

    return {
      size: seriesSize,
      strokeColors: '#fff',
      strokeWidth: seriesSize > 0 ? 2 : 0,
      strokeOpacity: seriesSize > 0 ? 0.9 : 0,
      fillOpacity: seriesSize > 0 ? 1 : 0,
      shape: 'square',
      showNullDataPoints: false,
    };
  }

  /**
   * Builds data-label config for series (+ optional trend line) using current cosmetic options.
   * When alternateTrendLines is true, even indices are measured series and odd indices are trend lines.
   */
  protected buildSeriesTrendDataLabelsConfig(
    seriesOption: SeriesDataLabelOption,
    trendLineOption: TrendLineDataLabelOption,
    options: {
      hasTrendLine: boolean;
      seriesCount: number;
      alternateTrendLines?: boolean;
    }
  ): any {
    const { hasTrendLine, seriesCount, alternateTrendLines = false } = options;

    const optionForIndex = (
      index: number
    ): SeriesDataLabelOption | TrendLineDataLabelOption => {
      if (!hasTrendLine) {
        return seriesOption;
      }
      if (alternateTrendLines) {
        return index % 2 === 0 ? seriesOption : trendLineOption;
      }
      if (seriesCount <= 2) {
        return index === 0 ? seriesOption : trendLineOption;
      }
      return seriesOption;
    };

    const enabled: number[] = [];
    for (let i = 0; i < seriesCount; i++) {
      const option = optionForIndex(i);
      if (this.optionShowsMarkers(option) || this.optionShowsValues(option)) {
        enabled.push(i);
      }
    }

    const useExplicitEnabledOnSeries =
      hasTrendLine || enabled.length !== seriesCount;

    return {
      enabled: enabled.length > 0,
      enabledOnSeries: useExplicitEnabledOnSeries ? enabled : undefined,
      formatter: (value: any, ctx: any) => {
        const seriesIndex = ctx?.seriesIndex ?? 0;
        const dataPointIndex = ctx?.dataPointIndex ?? 0;
        const seriesData = ctx?.w?.config?.series?.[seriesIndex]?.data;
        const option = optionForIndex(seriesIndex);

        if (!this.shouldShowValueAtIndex(option, dataPointIndex, seriesData)) {
          return '';
        }
        return value || value === 0 ? value : '';
      },
    };
  }

  /**
   * Builds marker config for series (+ optional trend line), including discrete first/last markers.
   */
  protected buildSeriesTrendMarkersConfig(
    seriesOption: SeriesDataLabelOption,
    trendLineOption: TrendLineDataLabelOption,
    options: {
      hasTrendLine: boolean;
      seriesCount: number;
      alternateTrendLines?: boolean;
      seriesDataList?: any[][];
      colors?: string[];
    }
  ): ApexMarkers {
    const {
      hasTrendLine,
      seriesCount,
      alternateTrendLines = false,
      seriesDataList = [],
      colors = [],
    } = options;

    const optionForIndex = (index: number): SeriesDataLabelOption | TrendLineDataLabelOption => {
      if (!hasTrendLine) {
        return seriesOption;
      }
      if (alternateTrendLines) {
        return index % 2 === 0 ? seriesOption : trendLineOption;
      }
      if (seriesCount <= 2) {
        return index === 0 ? seriesOption : trendLineOption;
      }
      return seriesOption;
    };

    const sizeForOption = (option: SeriesDataLabelOption | TrendLineDataLabelOption): number => {
      if (!this.optionShowsMarkers(option) || this.optionShowsEndpointsOnly(option)) {
        return 0;
      }
      return 3;
    };

    const sizes: number[] = [];
    const strokeWidths: number[] = [];
    const strokeOpacities: number[] = [];
    const fillOpacities: number[] = [];
    const discrete: NonNullable<ApexMarkers['discrete']> = [];

    for (let i = 0; i < seriesCount; i++) {
      const option = optionForIndex(i);
      const size = sizeForOption(option);
      sizes.push(size);
      strokeWidths.push(size > 0 ? 2 : 0);
      strokeOpacities.push(size > 0 ? 0.9 : 0);
      fillOpacities.push(size > 0 ? 1 : 0);

      if (this.optionShowsEndpointsOnly(option)) {
        const color = colors[i] ?? this.defaultSeriesColors[i % this.defaultSeriesColors.length];
        discrete.push(
          ...this.buildDiscreteEndpointMarkers(i, seriesDataList[i], color)
        );
      }
    }

    const useArrays = hasTrendLine || seriesCount > 1;

    return {
      size: useArrays ? sizes : sizes[0] ?? 0,
      strokeColors: '#fff',
      strokeWidth: useArrays ? strokeWidths : strokeWidths[0] ?? 0,
      strokeOpacity: useArrays ? strokeOpacities : strokeOpacities[0] ?? 0,
      fillOpacity: useArrays ? fillOpacities : fillOpacities[0] ?? 0,
      shape: 'square',
      showNullDataPoints: false,
      // ApexCharts reads discrete.length without a null guard in places;
      // keep an empty array instead of undefined.
      discrete,
    };
  }

  loadDurationTotalGraphOptions(graphData: GraphDataModel, displayDataPointOption: SeriesDataLabelOption = SeriesDataLabelOption.DotsAndValues, displayLegend: boolean = true, displayAnnotations: boolean = true, staffMembers: DataTakerStaffDataModel[] = []) {
    this.chartOptions = {
      series: graphData.Series!.map((series, index) =>
        this.mapLineSeriesForChart(series, graphData, index)
      ),
      colors: this.resolveChartColors(graphData.Series),
      legend: this.buildLegendConfig(displayLegend),
      chart: {
        height: 350,
        type: 'line',
        ...this.sharedChartConfig,
        events: this.createAnnotationReorderEvent(),
      },
      dataLabels: this.getDataLabelsConfig(displayDataPointOption),
      stroke: {
        curve: 'straight',
      },
      grid: {
        padding: {
          right: 30,
          left: 20,
        },
      },
      markers: this.getMarkersConfig(displayDataPointOption),
      tooltip: {
        custom: this.createCustomTooltipSimple(graphData, staffMembers),
      },
      title: {
        text: graphData.Title ? graphData.Title : 'Chart',
      },
      xaxis: {
        type: 'datetime',
        title: {
          text: graphData.XAxisTitle ?? 'X Axis',
        },
        labels: {
          datetimeUTC: false,
        },
      },
      yaxis: {
        title: {
          text: graphData.YAxisTitle ?? 'Y Axis',
        },
        labels: {
          formatter: (value: number) => {
            // If it's a whole number, don't show decimal places
            if (Number.isInteger(value)) {
              return value.toString();
            }
            // If it's a decimal, round to 2 decimal places
            return value.toFixed(2);
          }
        }
      },
      annotations: this.buildAnnotationsConfig(graphData, null, displayAnnotations),
    };
  }

  loadDefaultLineGraphOptions(
    graphData: GraphDataModel, 
    displayDataPointOption: SeriesDataLabelOption = SeriesDataLabelOption.DotsAndValues, 
    displayLegend: boolean = true, 
    displayAnnotations: boolean = true, 
    displayTrendLineForProgramGraph: boolean = false,
    limitYAxisValues: boolean = false,
    displayTotalSessionTimeAndTargetDataTimeInTooltip: boolean = false,
    displayPromptDataInTooltip: boolean = false,
    staffMembers: DataTakerStaffDataModel[] = [],
  ) {
    if (this.isGrouped()) { // Grouped Graph or Normal Graphs
      console.log('Grouped Graph');
      this.isGrouped.set(true);
      this.chartOptions = {
        series: graphData.Series!.map((series, index) =>
          this.mapLineSeriesForChart(series, graphData, index)
        ),
        colors: this.resolveChartColors(graphData.Series),
        legend: this.buildLegendConfig(displayLegend),
        chart: {
          height: 350,
          type: 'line',
          ...this.sharedChartConfig,
          events: this.createAnnotationReorderEvent(),
        },
        dataLabels: this.getDataLabelsConfig(displayDataPointOption),
        stroke: {
          curve: 'straight',
        },
        grid: {
          padding: {
            right: 30,
            left: 20,
          },
        },
        markers: this.getMarkersConfig(displayDataPointOption),
        tooltip: displayTotalSessionTimeAndTargetDataTimeInTooltip || displayPromptDataInTooltip ? {
          custom: displayPromptDataInTooltip ? this.createCustomTooltipPrompt(graphData, staffMembers) : this.createCustomTooltipTime(graphData)
        } : staffMembers.length > 0 ? {
          custom: this.createCustomTooltipSimple(graphData, staffMembers)
        } : {
          y: {
            formatter: (value: any) => {
              if (value === null) {
                return '';
              }
              return value;
            },
          },
        },
        title: {
          text: graphData.Title ?? 'Chart',
        },
        xaxis: {
          type: 'datetime',
          title: {
            text: graphData.XAxisTitle ?? 'X Axis',
          },
          labels: {
            datetimeUTC: false,
          },
        },
        yaxis: {
          min: limitYAxisValues ? 0 : undefined,
          max: limitYAxisValues ? 100 : undefined,
          title: {
            text: graphData.YAxisTitle ?? 'Y Axis',
          },
          labels: {
            formatter: (value: number) => {
              // If it's a whole number, don't show decimal places
              if (Number.isInteger(value)) {
                return value.toString();
              }
              // If it's a decimal, round to 2 decimal places
              return value.toFixed(2);
            }
          }
        },
        annotations: this.buildAnnotationsConfig(graphData, null, displayAnnotations),
      };
    } else { // Synced Graphs
      this.isGrouped.set(false);
      // if it is a program graph and trend line is selected, we need to graph types two by two;
      // every other graph in the series will be the trend line in this case;
      if (displayTrendLineForProgramGraph) { // Program Graph with Trend Line
        console.log('Program Graph with Trend Line');
        var allSeries = graphData.Series!;
        var notTrendLineSeries = allSeries.filter((series, index) => index % 2 === 0);
        var trendLineSeries = allSeries.filter((series, index) => index % 2 === 1);

        this.chartsOptions = notTrendLineSeries.map((series, trendMapIndex) => {
          var trendLineSeriesForGraph = trendLineSeries.shift();
          const fixedSeriesIndex = trendMapIndex * 2;
          return {
            series: [{
              name: series.Name ?? 'No Name',
              data: this.processSeriesDataWithBreaks(series, graphData).data,
              color: this.resolveSeriesColor(series, fixedSeriesIndex),
            },
            // pop the first series from the trendLineSeries array and add it to the series array
            {
              name: trendLineSeriesForGraph!.Name ?? 'No Name',
              data: this.processSeriesDataWithBreaks(trendLineSeriesForGraph!, graphData).data,
              color: this.resolveSeriesColor(trendLineSeriesForGraph, fixedSeriesIndex + 1),
            },
          ],
            colors: [
              this.resolveSeriesColor(series, fixedSeriesIndex),
              this.resolveSeriesColor(trendLineSeriesForGraph, fixedSeriesIndex + 1),
            ],
            legend: this.buildLegendConfig(displayLegend),
            chart: {
              id: series.Name ?? 'No Name',
              group: 'group1',
              height: 350,
              type: 'line',
              ...this.sharedChartConfig,
              events: this.createAnnotationReorderEvent(),
            },
            dataLabels: this.getDataLabelsConfig(displayDataPointOption),
            stroke: {
              curve: 'straight',
            },
            grid: {
              padding: {
                right: 30,
                left: 20,
              },
            },
            markers: this.getMarkersConfig(displayDataPointOption),
            tooltip: displayTotalSessionTimeAndTargetDataTimeInTooltip || displayPromptDataInTooltip ? {
              custom: displayPromptDataInTooltip ? this.createCustomTooltipPrompt(graphData, staffMembers, fixedSeriesIndex) : this.createCustomTooltipTime(graphData)
            } : {
              x: {
                format: 'MMM dd yyyy',
              },
              y: {
                formatter: (value: any) => {
                  if (value === null) {
                    return '';
                  }
                  return value;
                },
              },
            },
            title: {
              text: series.Name ?? 'Chart',
            },
            xaxis: {
              type: 'datetime',
              title: {
                text: graphData.XAxisTitle ?? 'X Axis',
              },
              labels: {
                datetimeUTC: false,
              },
            },
            yaxis: {
              min: limitYAxisValues ? 0 : undefined,
              max: limitYAxisValues ? 100 : undefined,
              title: {
                text: graphData.YAxisTitle ?? 'Y Axis',
                },
              labels: {
                formatter: (value: number) => {
                  // If it's a whole number, don't show decimal places
                  if (Number.isInteger(value)) {
                    return value.toString();
                  }
                  // If it's a decimal, round to 2 decimal places
                  return value.toFixed(2);
                }
              }
              },
            annotations: this.buildAnnotationsConfig(graphData, series, displayAnnotations),
          }
        });
      } else { // Synced Graphs 
        console.log('Synced Graphs');
        this.chartsOptions = graphData.Series!.map((series, seriesIndex) => {
          return {
          series: [{
            name: series.Name ?? 'No Name',
            data: this.processSeriesDataWithBreaks(series, graphData).data,
            color: this.resolveSeriesColor(series, seriesIndex),
          }],
          colors: [this.resolveSeriesColor(series, seriesIndex)],
          legend: this.buildLegendConfig(displayLegend),
          chart: {
            id: series.Name ?? 'No Name',
            group: 'group1',
            height: 350,
            type: 'line',
            ...this.sharedChartConfig,
            events: this.createAnnotationReorderEvent(),
          },
          dataLabels: this.getDataLabelsConfig(displayDataPointOption),
          stroke: {
            curve: 'straight',
          },
          grid: {
            padding: {
              right: 30,
              left: 20,
            },
          },
          markers: this.getMarkersConfig(displayDataPointOption),
          tooltip: displayTotalSessionTimeAndTargetDataTimeInTooltip || displayPromptDataInTooltip ? {
            custom: displayPromptDataInTooltip ? this.createCustomTooltipPrompt(graphData, staffMembers, seriesIndex) : this.createCustomTooltipTime(graphData)
          } : staffMembers.length > 0 ? {
            custom: this.createCustomTooltipSimple(graphData, staffMembers)
          } : {
            x: {
              format: 'MMM dd yyyy',
            },
            y: {
              formatter: (value: any) => {
                if (value === null) {
                  return '';
                }
                return value;
              },
            },
          },
          title: {
            text: series.Name ?? 'Chart',
          },
          xaxis: {
            type: 'datetime',
            title: {
              text: graphData.XAxisTitle ?? 'X Axis',
            },
            labels: {
              datetimeUTC: false,
            },
          },
          yaxis: {
            min: limitYAxisValues ? 0 : undefined,
            max: limitYAxisValues ? 100 : undefined,
            title: {
              text: graphData.YAxisTitle ?? 'Y Axis',
              },
            labels: {
              formatter: (value: number) => {
                // If it's a whole number, don't show decimal places
                if (Number.isInteger(value)) {
                  return value.toString();
                }
                // If it's a decimal, round to 2 decimal places
                return value.toFixed(2);
              }
            }
            },
          annotations: displayAnnotations
            ? {
                xaxis: this.xaxisAnnotationsFromGraphDataForSyncedTargets(graphData, series),
                yaxis: this.yaxisAnnotationsFromGraphDataForSyncedTargets(graphData, series),
                points: this.pointsAnnotationsFromGraphDataForSyncedTargets(graphData, series),
                texts: this.textsAnnotationsFromGraphDataForSyncedTargets(graphData, series),
              }
            : { xaxis: [], yaxis: [], points: [], texts: [] },
        }
        });
      }
    }
  }

  loadTimeOfDayGraphOptions(graphData: GraphDataModel, displayAnnotations: boolean = true, staffMembers: DataTakerStaffDataModel[] = []) {
    this.chartOptions = {
      series: graphData.Series!.map((series) => ({
        name: series.Name ?? 'No Name',
        data: series.DataPoints!.map(
          (data) =>
            [
              data.Date ? new Date(data.Date).getTime() : new Date().getTime(),
              data.Value,
            ] as [number, number | null]
        ),
        color: series.Color ?? '',
      })),
      chart: {
        height: 350,
        type: 'scatter',
        zoom: {
          type: 'xy',
        },
        events: this.createAnnotationReorderEvent(),
      },
      dataLabels: {
        enabled: false,
      },
      grid: {
        xaxis: {
          lines: {
            show: true,
          },
        },
        yaxis: {
          lines: {
            show: true,
          },
        },
      },
      xaxis: {
        type: 'datetime',
        labels: {
          datetimeUTC: false,
        },
      },
      yaxis: {
        labels: {
          formatter: (value) => {
            // format time from seconds to HH:MM:SS
            return Utils.formatTime(value);
          },
        },
      },
      tooltip: {
        custom: this.createCustomTooltipTimeOfDay(graphData, staffMembers),
      },
      annotations: this.buildAnnotationsConfig(graphData, null, displayAnnotations),
    };
  }

  /**
   * Curated high-contrast categorical colors for declutter mode.
   * Ordered for strong hue separation (avoids near-duplicate teals/blues).
   */
  protected readonly declutterColors: string[] = [
    '#E6194B', // red
    '#3CB44B', // green
    '#4363D8', // blue
    '#F58231', // orange
    '#911EB4', // purple
    '#42D4F4', // cyan
    '#F032E6', // magenta
    '#BFEF45', // lime
    '#9A6324', // brown
    '#469990', // teal
    '#E6BEFF', // lavender
    '#800000', // maroon
    '#AAFFC3', // mint
    '#808000', // olive
    '#FFD8B1', // apricot
    '#000075', // navy
    '#A9F971', // yellow-green
    '#FF6B6B', // coral
    '#4ECDC4', // turquoise
    '#C44569', // rose
  ];

  /**
   * Picks a declutter color that stays visually distinct even when many annotations
   * are visible. Uses the curated palette while it fits; otherwise spaces hues evenly.
   */
  protected resolveDeclutterColorAtIndex(index: number, totalCount: number): string {
    if (totalCount <= this.declutterColors.length && index < this.declutterColors.length) {
      return this.declutterColors[index];
    }

    const count = Math.max(totalCount, 1);
    const hue = Math.round(((index * 360) / count) % 360);
    // Alternate saturation/lightness slightly so adjacent hues still read apart on dashed lines.
    const saturation = 78 - (index % 3) * 8;
    const lightness = 42 + (index % 2) * 10;
    return this.hslToHex(hue, saturation, lightness);
  }

  protected hslToHex(hue: number, saturation: number, lightness: number): string {
    const s = Math.min(100, Math.max(0, saturation)) / 100;
    const l = Math.min(100, Math.max(0, lightness)) / 100;
    const a = s * Math.min(l, 1 - l);
    const f = (n: number) => {
      const k = (n + hue / 30) % 12;
      const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
      return Math.round(255 * color)
        .toString(16)
        .padStart(2, '0');
    };
    return `#${f(0)}${f(8)}${f(4)}`.toUpperCase();
  }

  /**
   * Returns a stable key -> color map so that decluttered annotations keep a consistent,
   * distinct color across the different annotation-building functions (x-axis, y-axis, etc.)
   */
  protected buildDeclutterColorMap(annotations: GraphAnnotationModel[]): Map<string, string> {
    const colorMap = new Map<string, string>();
    const visibleAnnotations = [...annotations].sort((a, b) =>
      this.annotationDisplayKey(a).localeCompare(this.annotationDisplayKey(b))
    );
    const totalCount = visibleAnnotations.length;

    visibleAnnotations.forEach((annotation, index) => {
      colorMap.set(
        this.annotationDisplayKey(annotation),
        this.resolveDeclutterColorAtIndex(index, totalCount)
      );
    });

    return colorMap;
  }

  /**
   * Resolves the color to use for an annotation's line/region/marker, honoring the
   * "Declutter Annotations" toggle which overrides each annotation with a distinct color.
   */
  protected resolveAnnotationColor(
    annotation: GraphAnnotationModel,
    fallback: string = '#008FFB',
    declutterCandidates: GraphAnnotationModel[] = []
  ): string {
    if (this.declutterAnnotations()) {
      const candidates =
        declutterCandidates.length > 0
          ? declutterCandidates
          : this.annotations().filter((item) => this.isAnnotationDisplayed(item));
      const colorMap = this.buildDeclutterColorMap(candidates);
      return colorMap.get(this.annotationDisplayKey(annotation)) ?? fallback;
    }
    return annotation.Color ?? fallback;
  }

  /**
   * Returns the label text for an annotation, hiding it entirely when annotations are
   * decluttered (the separate color legend is used to identify them instead).
   */
  protected resolveAnnotationLabelText(
    annotation: GraphAnnotationModel,
    text: string
  ): string | string[] {
    if (this.declutterAnnotations()) {
      return '';
    }
    return this.formatAnnotationLabelText(text);
  }

  /**
   * Reads the rendered ApexCharts plot size when available so label placement can
   * target the true center of the annotation line.
   */
  protected getChartPlotSize(): { width: number; height: number } {
    try {
      const apexChart = this.resolveApexChartsInstance(this.getChartComponent());
      const globals = apexChart?.w?.globals;
      const width = Number(globals?.gridWidth);
      const height = Number(globals?.gridHeight);
      return {
        width: Number.isFinite(width) && width > 0 ? width : 640,
        height: Number.isFinite(height) && height > 0 ? height : 320,
      };
    } catch {
      return { width: 640, height: 320 };
    }
  }

  /**
   * X-axis annotations are vertical lines with rotated labels. Start sits near the top of
   * the plot, end near the bottom. Center is offset from the top by a quarter of the plot
   * height so the rotated box does not sit on the x-axis.
   */
  protected resolveXAxisAnnotationLabelPlacement(annotation: GraphAnnotationModel): {
    position: 'top' | 'bottom';
    offsetY: number;
  } {
    const { height } = this.getChartPlotSize();
    switch (resolveAnnotationLabelPositionValue(annotation)) {
      case 'start':
        return { position: 'top', offsetY: 8 };
      case 'end':
        return { position: 'bottom', offsetY: -8 };
      default:
        return { position: 'top', offsetY: Math.round(height / 4) };
    }
  }

  /**
   * Y-axis annotations are horizontal lines. Use left/right anchors for start/end and a
   * full-plot offset for center so the label sits mid-line instead of near start.
   */
  protected resolveYAxisAnnotationLabelPlacement(annotation: GraphAnnotationModel): {
    position: 'left' | 'right';
    offsetX: number;
    textAnchor: 'start' | 'middle' | 'end';
  } {
    const { width } = this.getChartPlotSize();
    switch (resolveAnnotationLabelPositionValue(annotation)) {
      case 'start':
        return { position: 'left', offsetX: 16, textAnchor: 'start' };
      case 'end':
        return { position: 'right', offsetX: -16, textAnchor: 'end' };
      default:
        return {
          position: 'left',
          offsetX: Math.round(width),
          textAnchor: 'middle',
        };
    }
  }

  /**
   * Child graphs call this after assigning chartOptions. Register shared chart
   * events on every graph configuration before Angular passes the inputs to Apex.
   * Annotation and series updates must still go through the apx-chart inputs (or
   * the add/remove APIs below); calling updateOptions/add* here raced with
   * ng-apexcharts destroying the chart whenever any non-series input changed.
   */
  protected applyChartOptions(
    _chart: ChartComponent | undefined | null,
    options: Partial<ChartOptions>
  ): void {
    if (!options.chart) {
      return;
    }

    options.chart = {
      ...options.chart,
      events: {
        ...(options.chart.events ?? {}),
        ...this.createAnnotationReorderEvent(),
      },
    };
  }

  /**
   * Re-applies the full visible annotation set on an already-rendered chart.
   * Used for group toggles, declutter, and label-style changes. Mutates the
   * existing annotations object so Angular does not recreate the chart.
   */
  protected refreshChartAnnotations(): void {
    const graphData = this.getGraphDataForAnnotations();
    if (!graphData) {
      this.updateGraph();
      return;
    }

    const display = this.shouldRenderAnnotations();
    this.forEachAnnotationTarget((chart, options, series) => {
      const next = this.buildAnnotationsConfig(graphData, series, display);
      const api = this.resolveAnnotationChartApi(chart);
      if (api && (typeof api.clearAnnotations === 'function' || this.hasAnnotationMutators(api))) {
        this.patchChartOptionsAnnotations(options, next, 'replace');
        this.applyChartAnnotations(chart, options.annotations);
        return;
      }

      // No live ApexCharts instance: give apx-chart a new annotations object so it redraws.
      options.annotations = next;
    });
  }

  /**
   * Show a single annotation with add* only. Hide rebuilds the visible set:
   * ApexCharts removeAnnotation uses a CSS class matching `id`, which does not
   * work for annotations drawn from the original chart options.
   */
  protected toggleChartAnnotation(annotation: GraphAnnotationModel, isDisplayed: boolean): void {
    const graphData = this.getGraphDataForAnnotations();
    if (!graphData) {
      this.updateGraph();
      return;
    }

    if (!isDisplayed) {
      this.refreshChartAnnotations();
      return;
    }

    if (!this.shouldRenderAnnotations()) {
      return;
    }

    this.forEachAnnotationTarget((chart, options, series) => {
      const next = this.buildAnnotationsConfig(
        { ...graphData, Annotations: [annotation] },
        series,
        true
      );
      this.addChartAnnotations(chart, next);
      this.patchChartOptionsAnnotations(options, next, 'merge');
    });
  }

  protected applyChartAnnotations(
    chart: ChartComponent | undefined | null,
    annotations: ApexAnnotations | undefined
  ): void {
    const instance = this.resolveApexChartsInstance(chart);
    const api = instance ?? this.resolveAnnotationChartApi(chart);
    if (!api) {
      return;
    }

    const normalized = this.normalizeChartAnnotations(annotations);
    this.syncApexAnnotationConfig(instance ?? api, normalized);

    try {
      if (typeof api.clearAnnotations === 'function') {
        api.clearAnnotations();
      }
    } catch {
      // Chart may not be fully mounted yet.
    }

    this.addChartAnnotations(chart, annotations, true);
  }

  protected addChartAnnotations(
    chart: ChartComponent | undefined | null,
    annotations: ApexAnnotations | undefined,
    replaceTexts: boolean = false
  ): void {
    const api = this.resolveAnnotationChartApi(chart);
    if (!api) {
      return;
    }

    const normalized = this.normalizeChartAnnotations(annotations);
    const xaxis = (normalized.xaxis ?? []).filter((item) => this.isRenderableApexAnnotation(item));
    const yaxis = (normalized.yaxis ?? []).filter((item) => this.isRenderableApexAnnotation(item));
    const points = (normalized.points ?? []).filter((item) => this.isRenderableApexAnnotation(item));
    const texts = ((normalized as { texts?: unknown[] }).texts ?? []).filter((item) =>
      this.isRenderableApexAnnotation(item)
    );

    const canAdd =
      typeof api.addXaxisAnnotation === 'function' &&
      typeof api.addYaxisAnnotation === 'function' &&
      typeof api.addPointAnnotation === 'function';

    if (canAdd) {
      const pushToMemory = !replaceTexts;
      xaxis.forEach((item) => api.addXaxisAnnotation(item, pushToMemory));
      yaxis.forEach((item) => api.addYaxisAnnotation(item, pushToMemory));
      points.forEach((item) => api.addPointAnnotation(item, pushToMemory));
      if (texts.length > 0 || replaceTexts) {
        const instance = this.resolveApexChartsInstance(chart) ?? this.resolveApexChartsInstance(api);
        if (instance?.w?.config?.annotations) {
          instance.w.config.annotations.texts = replaceTexts
            ? texts
            : [...(instance.w.config.annotations.texts ?? []), ...texts];
        }
      }
      return;
    }

    if (typeof api.updateOptions === 'function') {
      api.updateOptions({ annotations: { xaxis, yaxis, points, texts } }, false, false);
    }
  }

  protected removeChartAnnotation(chart: ChartComponent | undefined | null, id: string): void {
    const api = this.resolveAnnotationChartApi(chart);
    try {
      if (typeof api?.removeAnnotation === 'function') {
        api.removeAnnotation(id);
      }
    } catch {
      // Annotation may not be present on this chart instance.
    }
  }

  protected syncApexAnnotationConfig(api: any, annotations: ApexAnnotations): void {
    const instance = this.resolveApexChartsInstance(api) ?? api;
    if (!instance?.w?.config) {
      return;
    }

    instance.w.config.annotations = {
      ...(instance.w.config.annotations ?? {}),
      xaxis: annotations.xaxis ?? [],
      yaxis: annotations.yaxis ?? [],
      points: annotations.points ?? [],
      texts: annotations.texts ?? [],
    };
  }

  /**
   * Prefer the live ApexCharts instance (chartInstance()) over ChartComponent
   * wrappers. ChartComponent.chart is the options input signal, not the chart.
   */
  protected resolveAnnotationChartApi(chart: unknown): any {
    const instance = this.resolveApexChartsInstance(chart);
    if (instance && (this.hasAnnotationMutators(instance) || typeof instance.clearAnnotations === 'function')) {
      return instance;
    }

    if (!chart || typeof chart !== 'object') {
      return null;
    }

    const candidate = chart as any;
    if (this.hasAnnotationMutators(candidate) || typeof candidate.clearAnnotations === 'function') {
      return candidate;
    }

    return candidate;
  }

  protected resolveApexChartsInstance(chart: unknown): any {
    if (!chart || typeof chart !== 'object') {
      return null;
    }

    const candidate = chart as any;
    if (candidate.w?.globals) {
      return candidate;
    }

    if (typeof candidate.chartInstance === 'function') {
      const instance = candidate.chartInstance();
      if (instance?.w?.globals || this.hasAnnotationMutators(instance) || typeof instance?.clearAnnotations === 'function') {
        return instance;
      }
    }

    const nested = candidate.chart;
    if (nested && typeof nested !== 'function') {
      if (nested.w?.globals || this.hasAnnotationMutators(nested) || typeof nested.clearAnnotations === 'function') {
        return nested;
      }
    }

    return null;
  }

  protected hasAnnotationMutators(value: unknown): boolean {
    const candidate = value as any;
    return (
      !!candidate &&
      typeof candidate.addXaxisAnnotation === 'function' &&
      typeof candidate.addYaxisAnnotation === 'function' &&
      typeof candidate.addPointAnnotation === 'function'
    );
  }

  protected forEachAnnotationTarget(
    fn: (
      chart: ChartComponent | undefined | null,
      options: Partial<ChartOptions>,
      series: GraphSeriesModel | null
    ) => void
  ): void {
    const graphData = this.getGraphDataForAnnotations();
    const syncedCharts = this.getSyncedChartComponents();

    if (this.isGrouped() || !this.chartsOptions?.length) {
      fn(this.getChartComponent() ?? syncedCharts[0], this.chartOptions, null);
      return;
    }

    this.chartsOptions.forEach((opts, index) => {
      const series = graphData
        ? this.resolveSeriesForChartOptions(opts, graphData) ?? graphData.Series?.[index] ?? null
        : null;
      fn(syncedCharts[index] ?? this.getChartComponent(), opts, series);
    });
  }

  protected patchChartOptionsAnnotations(
    options: Partial<ChartOptions> | undefined,
    next: ApexAnnotations,
    mode: 'replace' | 'merge' | 'remove',
    removeId?: string
  ): void {
    if (!options) {
      return;
    }

    const current = (options.annotations ??= {
      xaxis: [],
      yaxis: [],
      points: [],
      texts: [],
    }) as ApexAnnotations & { texts?: unknown[] };

    if (mode === 'replace') {
      current.xaxis = next.xaxis ?? [];
      current.yaxis = next.yaxis ?? [];
      current.points = next.points ?? [];
      current.texts = next.texts ?? [];
      return;
    }

    if (mode === 'remove' && removeId) {
      const withoutId = (items: any[] | undefined) =>
        (items ?? []).filter((item) => item?.id !== removeId);
      current.xaxis = withoutId(current.xaxis);
      current.yaxis = withoutId(current.yaxis);
      current.points = withoutId(current.points);
      current.texts = withoutId(current.texts as any[]);
      return;
    }

    (['xaxis', 'yaxis', 'points', 'texts'] as const).forEach((bucket) => {
      const incoming = ((next as any)[bucket] ?? []).filter((item: unknown) =>
        this.isRenderableApexAnnotation(item)
      );
      const existing = (((current as any)[bucket] ?? []) as any[]).slice();
      incoming.forEach((item: any) => {
        const idx = existing.findIndex((row) => row?.id === item.id);
        if (idx >= 0) {
          existing[idx] = item;
        } else {
          existing.push(item);
        }
      });
      (current as any)[bucket] = existing;
    });
  }

  protected isRenderableApexAnnotation(item: unknown): boolean {
    return !!item && typeof item === 'object' && Object.keys(item as object).length > 0;
  }

  protected normalizeChartAnnotations(
    annotations: ApexAnnotations | undefined | null
  ): ApexAnnotations {
    return annotations
      ? {
          xaxis: annotations.xaxis ?? [],
          yaxis: annotations.yaxis ?? [],
          points: annotations.points ?? [],
          texts: annotations.texts ?? [],
        }
      : { xaxis: [], yaxis: [], points: [], texts: [] };
  }

  protected clearChartAnnotations(chart: ChartComponent): void {
    try {
      const api = this.resolveAnnotationChartApi(chart);
      if (typeof api?.clearAnnotations === 'function') {
        api.clearAnnotations();
      }
    } catch {
      // Chart may not be fully mounted yet.
    }
  }

  protected getGraphDataForAnnotations(): GraphDataModel | null {
    const graphData = (this as { graphData?: () => GraphDataModel | null }).graphData;
    if (typeof graphData === 'function') {
      return graphData() ?? null;
    }
    return null;
  }

  protected getChartComponent(): ChartComponent | undefined | null {
    return (this as { chart?: ChartComponent }).chart;
  }

  protected getSyncedChartComponents(): ChartComponent[] {
    const chartList = (this as { chartList?: { toArray?: () => ChartComponent[] } }).chartList;
    if (typeof chartList?.toArray === 'function') {
      return chartList.toArray();
    }
    return [];
  }

  protected buildAnnotationsConfig(
    graphData: GraphDataModel,
    series: GraphSeriesModel | null = null,
    display: boolean = true
  ) {
    if (!display) {
      return { xaxis: [], yaxis: [], points: [], texts: [] };
    }

    return {
      xaxis: this.xaxisAnnotationsFromGraphData(graphData, series),
      yaxis: this.yaxisAnnotationsFromGraphData(graphData, series),
      points: this.pointsAnnotationsFromGraphData(graphData, series),
      texts: this.textsAnnotationsFromGraphData(graphData, series),
    };
  }

  protected resolveAnnotationLineBorderWidth(): number {
    return this.declutterAnnotations() ? 3 : 1;
  }

  protected resolveAnnotationLineStrokeOpacity(): number {
    return this.declutterAnnotations() ? 0.65 : 0.3;
  }

  protected resolveAnnotationPointMarkerSize(): number {
    return this.declutterAnnotations() ? 12 : 10;
  }

  protected resolveAnnotationPointMarkerStrokeWidth(): number {
    return this.declutterAnnotations() ? 3 : 1;
  }

  /**
   * Builds the label style object for an annotation, honoring the annotation label
   * background option (Solid/Transparent) and the annotation's configured font size.
   */
  protected buildAnnotationLabelStyle(
    annotation: GraphAnnotationModel,
    color: string
  ): { fontSize: string; fontWeight: string; fontFamily: string; color: string; opacity: number; background: string } {
    const fontSize = `${annotation.LabelFontSize ?? 14}px`;
    const solid = this.annotationLabelBackgroundOption() === AnnotationLabelBackgroundOption.Solid;

    if (solid) {
      return {
        fontSize,
        fontWeight: 'bold',
        fontFamily: 'monospace',
        color: '#fff',
        opacity: 1,
        background: color,
      };
    }

    return {
      fontSize,
      fontWeight: 'bold',
      fontFamily: 'monospace',
      color: `${color}80`,
      opacity: 0.2,
      background: 'rgba(255, 255, 255, 0.05)',
    };
  }

  protected resolveDeclutterLegendGroup(
    annotation: GraphAnnotationModel
  ): AnnotationColorLegendGroup {
    switch (annotation.GraphAnnotationType) {
      case GraphAnnotationType.XAxis:
      case GraphAnnotationType.XAxisRange:
        return 'x';
      case GraphAnnotationType.YAxis:
      case GraphAnnotationType.YAxisRange:
        return 'y';
      default:
        return 'other';
    }
  }

  protected formatDeclutterLegendDetail(annotation: GraphAnnotationModel): string {
    switch (annotation.GraphAnnotationType) {
      case GraphAnnotationType.XAxis:
        return annotation.StartDate
          ? new Date(annotation.StartDate).toLocaleDateString()
          : '';
      case GraphAnnotationType.XAxisRange: {
        const start = annotation.StartDate
          ? new Date(annotation.StartDate).toLocaleDateString()
          : '';
        const end = annotation.EndDate
          ? new Date(annotation.EndDate).toLocaleDateString()
          : '';
        return start && end ? `${start} - ${end}` : start || end;
      }
      case GraphAnnotationType.YAxis:
        return annotation.YValueStart != null ? `${annotation.YValueStart}` : '';
      case GraphAnnotationType.YAxisRange:
        if (annotation.YValueStart != null && annotation.YValueEnd != null) {
          return `${annotation.YValueStart} - ${annotation.YValueEnd}`;
        }
        return annotation.YValueStart != null ? `${annotation.YValueStart}` : '';
      case GraphAnnotationType.Point: {
        const date = annotation.StartDate
          ? new Date(annotation.StartDate).toLocaleDateString()
          : '';
        const value = annotation.YValueStart != null ? `${annotation.YValueStart}` : '';
        return date && value ? `${date} - ${value}` : date || value;
      }
      default:
        return '';
    }
  }

  /**
   * Returns the color legend items to display when annotations are decluttered, so users
   * can still tell which colored line/region corresponds to which annotation.
   * Colors stay assigned from the full displayed set so on-chart colors do not shift.
   * The listed items are limited to annotations actually shown on this graph/series
   * and inside the selected graph period.
   */
  getDeclutterLegendItems(
    graphData: GraphDataModel | null | undefined = null,
    series: GraphSeriesModel | null = null
  ): AnnotationColorLegendItem[] {
    if (!this.declutterAnnotations()) {
      return [];
    }

    const colorCandidates = this.resolveDeclutterAnnotationCandidates(graphData);
    const colorMap = this.buildDeclutterColorMap(colorCandidates);
    const { startTime, endTime } = this.getGraphPeriodBounds();
    const legendAnnotations = (
      series ? this.getAnnotationsShownOnGraph(graphData, series) : colorCandidates
    ).filter((annotation) => this.isAnnotationInGraphPeriod(annotation, startTime, endTime));
    const groupOrder: AnnotationColorLegendGroup[] = ['x', 'y', 'other'];

    return legendAnnotations
      .map((annotation) => {
        const group = this.resolveDeclutterLegendGroup(annotation);
        const sortKey =
          group === 'x' && annotation.StartDate
            ? new Date(annotation.StartDate).getTime()
            : Number.POSITIVE_INFINITY;

        return {
          label: annotation.Label ?? '',
          detail: this.formatDeclutterLegendDetail(annotation),
          color: colorMap.get(this.annotationDisplayKey(annotation)) ?? '#008FFB',
          group,
          sortKey: Number.isFinite(sortKey) ? sortKey : Number.POSITIVE_INFINITY,
        };
      })
      .sort((a, b) => {
        const groupDiff = groupOrder.indexOf(a.group) - groupOrder.indexOf(b.group);
        if (groupDiff !== 0) {
          return groupDiff;
        }
        if (a.group === 'x' && a.sortKey !== b.sortKey) {
          return a.sortKey - b.sortKey;
        }
        return a.label.localeCompare(b.label);
      })
      .map(({ sortKey: _sortKey, ...item }) => item);
  }

  getDeclutterLegendItemsForChart(
    graphData: GraphDataModel | null | undefined,
    chartOptions: Partial<ChartOptions> | undefined
  ): AnnotationColorLegendItem[] {
    if (!graphData || !chartOptions) {
      return [];
    }

    const series = this.resolveSeriesForChartOptions(chartOptions, graphData);
    if (!series) {
      return [];
    }

    return this.getDeclutterLegendItems(graphData, series);
  }

  protected getGraphPeriodBounds(): { startTime: number | null; endTime: number | null } {
    const startDate = (this as { startDate?: () => Date }).startDate;
    const endDate = (this as { endDate?: () => Date }).endDate;
    const start = typeof startDate === 'function' ? startDate() : null;
    const end = typeof endDate === 'function' ? endDate() : null;
    return {
      startTime: start instanceof Date ? start.getTime() : null,
      endTime: end instanceof Date ? end.getTime() : null,
    };
  }

  protected resolveSeriesForChartOptions(
    chartOptions: Partial<ChartOptions>,
    graphData: GraphDataModel
  ): GraphSeriesModel | null {
    const seriesList = graphData.Series ?? [];
    if (!seriesList.length) {
      return null;
    }

    const chartId = chartOptions.chart?.id;
    const chartGroup = chartOptions.chart?.group;
    const chartKey = typeof chartId === 'string' && chartGroup && chartId.startsWith(`${chartGroup}-`)
      ? chartId.slice(chartGroup.length + 1)
      : chartId;
    if (typeof chartKey === 'string' && chartKey.startsWith('target-')) {
      const targetId = Number(chartKey.slice('target-'.length));
      if (Number.isFinite(targetId)) {
        return seriesList.find((series) => series.PatientTargetID === targetId) ?? null;
      }
    }

    const firstSeries = Array.isArray(chartOptions.series) ? chartOptions.series[0] : null;
    const firstName =
      firstSeries && typeof firstSeries === 'object' && 'name' in firstSeries
        ? String((firstSeries as { name?: string }).name ?? '')
        : '';
    if (firstName) {
      const byName = seriesList.find((series) => (series.Name ?? 'No Name') === firstName);
      if (byName) {
        return byName;
      }
    }

    if (typeof chartKey === 'string' && chartKey) {
      return seriesList.find((series) => (series.Name ?? 'No Name') === chartKey) ?? null;
    }

    return null;
  }

  shouldDisplayDeclutterLegend(): boolean {
    return this.shouldRenderAnnotations() && this.declutterAnnotations();
  }

  protected getDeclutterLegendItemsForExport(): AnnotationColorLegendItem[][] {
    const graphData = this.getGraphDataForAnnotations();
    if (!graphData || !this.shouldDisplayDeclutterLegend()) {
      return [];
    }

    if (this.isGrouped() || !this.chartsOptions.length) {
      return [this.getDeclutterLegendItems(graphData)];
    }

    return this.chartsOptions.map((options) =>
      this.getDeclutterLegendItemsForChart(graphData, options)
    );
  }

  // Annotations
  xaxisAnnotationsFromGraphData(graphData: GraphDataModel, series: GraphSeriesModel | null = null) {
    var annotationsForXAxis = graphData?.Annotations?.filter((annotation) => annotation.GraphAnnotationType === GraphAnnotationType.XAxis || annotation.GraphAnnotationType === GraphAnnotationType.XAxisRange);
    
    // we need to filter the annotations to only include the ones for the target being graphed
    if (series?.PatientTargetID) {
      annotationsForXAxis = annotationsForXAxis?.filter((annotation) => 
        annotation.PatientTargetIDs?.includes(series.PatientTargetID!) || 
      ((annotation.PatientProgramIDs?.length ?? 0) > 0)
      );
    }

    const declutterCandidates = this.declutterAnnotations()
      ? this.resolveDeclutterAnnotationCandidates(graphData)
      : [];
    
    return annotationsForXAxis
      ?.filter((annotation) => this.isAnnotationDisplayed(annotation))
      .map((annotation) => {
        const color = this.resolveAnnotationColor(annotation, '#008FFB', declutterCandidates);
        const placement = this.resolveXAxisAnnotationLabelPlacement(annotation);
        if (annotation.GraphAnnotationType === GraphAnnotationType.XAxis) {
          return {
            id: this.annotationApexId(annotation),
            x: new Date(annotation.StartDate!).getTime(),
            strokeDashArray: 5,
            borderColor: color,
            strokeOpacity: this.resolveAnnotationLineStrokeOpacity(),
            borderWidth: this.resolveAnnotationLineBorderWidth(),
            label: {
              borderColor: color,
              style: { ...this.buildAnnotationLabelStyle(annotation, color), textAlign: 'left' },
              position: placement.position,
              offsetY: placement.offsetY,
              text: this.resolveAnnotationLabelText(annotation, annotation.Label ?? '') as any,
            },
          };
        }

        return {
          id: this.annotationApexId(annotation),
          x: new Date(annotation.StartDate!).getTime(),
          x2: new Date(annotation.EndDate!).getTime(),
          strokeDashArray: 5,
          borderColor: color,
          strokeOpacity: this.resolveAnnotationLineStrokeOpacity(),
          borderWidth: this.resolveAnnotationLineBorderWidth(),
          fillOpacity: 0.1,
          fillColor: color,
          label: {
            borderColor: color,
            style: { ...this.buildAnnotationLabelStyle(annotation, color), textAlign: 'left' },
            position: placement.position,
            offsetY: placement.offsetY,
            text: this.resolveAnnotationLabelText(annotation, annotation.Label ?? '') as any,
          },
        };
      }) ?? [];
  }

  yaxisAnnotationsFromGraphData(graphData: GraphDataModel, series: GraphSeriesModel | null = null) {
    var annotationsForYAxis = graphData?.Annotations?.filter((annotation) => annotation.GraphAnnotationType === GraphAnnotationType.YAxis || annotation.GraphAnnotationType === GraphAnnotationType.YAxisRange);
    
    // we need to filter the annotations to only include the ones for the target being graphed
    if (series?.PatientTargetID) {
      annotationsForYAxis = annotationsForYAxis?.filter((annotation) => 
        annotation.PatientTargetIDs?.includes(series.PatientTargetID!) || 
      ((annotation.PatientProgramIDs?.length ?? 0) > 0)
      );
    }

    const declutterCandidates = this.declutterAnnotations()
      ? this.resolveDeclutterAnnotationCandidates(graphData)
      : [];
    
    return annotationsForYAxis
      ?.filter((annotation) => this.isAnnotationDisplayed(annotation))
      .map((annotation) => {
        const color = this.resolveAnnotationColor(annotation, '#008FFB', declutterCandidates);
        const placement = this.resolveYAxisAnnotationLabelPlacement(annotation);
        if (annotation.GraphAnnotationType === GraphAnnotationType.YAxis) {
          return {
            id: this.annotationApexId(annotation),
            y: annotation.YValueStart,
            strokeDashArray: 5,
            borderColor: color,
            strokeOpacity: this.resolveAnnotationLineStrokeOpacity(),
            borderWidth: this.resolveAnnotationLineBorderWidth(),
            label: {
              borderColor: color,
              style: this.buildAnnotationLabelStyle(annotation, color),
              position: placement.position,
              offsetX: placement.offsetX,
              textAnchor: placement.textAnchor,
              text: this.resolveAnnotationLabelText(annotation, annotation.Label + ' - ' + annotation.YValueStart) as any,
            },
          };
        }

        return {
          id: this.annotationApexId(annotation),
          y: annotation.YValueStart,
          y2: annotation.YValueEnd,
          strokeDashArray: 5,
          borderColor: color,
          strokeOpacity: this.resolveAnnotationLineStrokeOpacity(),
          borderWidth: this.resolveAnnotationLineBorderWidth(),
          fillOpacity: 0.1,
          fillColor: color,
          label: {
            borderColor: color,
            style: this.buildAnnotationLabelStyle(annotation, color),
            position: placement.position,
            offsetX: placement.offsetX,
            textAnchor: placement.textAnchor,
            text: this.resolveAnnotationLabelText(annotation, annotation.Label + ' - ' + annotation.YValueStart + ' to ' + annotation.YValueEnd) as any,
          },
        };
      }) ?? [];
  }

  pointsAnnotationsFromGraphData(graphData: GraphDataModel, series: GraphSeriesModel | null = null) {
    var annotationsForPoints = graphData?.Annotations?.filter((annotation) => annotation.GraphAnnotationType === GraphAnnotationType.Point);
    
    // we need to filter the annotations to only include the ones for the target being graphed
    if (series?.PatientTargetID) {
      annotationsForPoints = annotationsForPoints?.filter((annotation) => 
        annotation.PatientTargetIDs?.includes(series.PatientTargetID!) || 
      ((annotation.PatientProgramIDs?.length ?? 0) > 0)
      );
    }

    const declutterCandidates = this.declutterAnnotations()
      ? this.resolveDeclutterAnnotationCandidates(graphData)
      : [];
    
    return annotationsForPoints
      ?.filter((annotation) => this.isAnnotationDisplayed(annotation))
      .map((annotation) => {
        const color = this.resolveAnnotationColor(annotation, '#008FFB', declutterCandidates);
        return {
          id: this.annotationApexId(annotation),
          x: new Date(annotation.StartDate!).getTime(),
          y: annotation.YValueStart,
          marker: {
            size: this.resolveAnnotationPointMarkerSize(),
            shape: 'circle',
            fillColor: 'transparent',
            strokeColor: color,
            strokeWidth: this.resolveAnnotationPointMarkerStrokeWidth(),
            strokeOpacity: this.resolveAnnotationLineStrokeOpacity(),
            strokeDashArray: 5,
            borderColor: color,
            borderWidth: this.resolveAnnotationLineBorderWidth(),
          },
          label: {
            borderColor: color,
            style: this.buildAnnotationLabelStyle(annotation, color),
            text: this.resolveAnnotationLabelText(annotation, annotation.Label + ' - ' + annotation.YValueStart) as any,
          },
        };
      }) ?? [];
  }

  textsAnnotationsFromGraphData(graphData: GraphDataModel, series: GraphSeriesModel | null = null) {
    var annotationsForTexts = graphData?.Annotations?.filter((annotation) => annotation.GraphAnnotationType === GraphAnnotationType.Text);
    
    // we need to filter the annotations to only include the ones for the target being graphed
    if (series?.PatientTargetID) {
      annotationsForTexts = annotationsForTexts?.filter((annotation) => 
        annotation.PatientTargetIDs?.includes(series.PatientTargetID!) || 
      ((annotation.PatientProgramIDs?.length ?? 0) > 0)
      );
    }

    const declutterCandidates = this.declutterAnnotations()
      ? this.resolveDeclutterAnnotationCandidates(graphData)
      : [];

    return annotationsForTexts
      ?.filter((annotation) => this.isAnnotationDisplayed(annotation))
      .map((annotation) => {
        const color = this.resolveAnnotationColor(annotation, '#008FFB', declutterCandidates);
        const style = this.buildAnnotationLabelStyle(annotation, color);
        return {
          id: this.annotationApexId(annotation),
          x: annotation.YValueStart ?? 50,
          y: annotation.YValueEnd ?? 50,
          text: this.resolveAnnotationLabelText(annotation, annotation.Label ?? 'Text Annotation') as any,
          fontSize: style.fontSize,
          fontWeight: style.fontWeight,
          fontFamily: style.fontFamily,
          opacity: style.opacity,
          background: style.background,
          textAlign: 'left',
          borderColor: color,
          borderWidth: 1,
        };
      }) ?? [];
  }

  createCustomTooltipTime(graphData: GraphDataModel, staffMembers: DataTakerStaffDataModel[] = []) {
    const self = this;
    return function({ series, seriesIndex, dataPointIndex, w }: { 
      series: any[], 
      seriesIndex: number, 
      dataPointIndex: number, 
      w: any 
    }) {
      const seriesName = graphData.Series![seriesIndex].Name ?? 'No Name';
      const originalDataPointIndex = self.getDataPointIndexForTooltip(seriesName, dataPointIndex);
      
      // If this is a break point (mapped to -1), don't show tooltip
      if (originalDataPointIndex === -1) {
        return null;
      }
      
      const dataPoint = graphData.Series![seriesIndex].DataPoints![originalDataPointIndex];
      const totalSessionTime = dataPoint.RawValue1 ? Math.floor(dataPoint.RawValue1 / 60) + 'h ' + (dataPoint.RawValue1 % 60).toFixed(2) + 'm' : 'N/A';
      const targetDataTime = dataPoint.RawValue2 ? Math.floor(dataPoint.RawValue2 / 60) + 'h ' + (dataPoint.RawValue2 % 60).toFixed(2) + 'm' : 'N/A';

      // create string of staff members names separated by commas
      const staffIDs = dataPoint.StaffIDs;
      const staffsForDataPoint = staffMembers.filter((staffMember) => staffIDs?.includes(staffMember.StaffID!));
      const staffMembersToDisplay = staffsForDataPoint.map((staffMember) => staffMember.FullName || 'No User').join(', ');
      const finalStaffDisplay = staffMembersToDisplay || 'No User';
      const staffMembersHTML = '<div class="tooltip-item">' +
        '<span class="tooltip-label">Staff Members:</span>' +
        '<span class="tooltip-value">' + finalStaffDisplay + '</span>' +
        '</div>';

      const dateToDisplay = new Date(dataPoint.Date ?? new Date().getTime()).toDateString();
      const seriesColor = w.globals.colors[seriesIndex%w.globals.colors.length] ?? '#008FFB';

      if (dataPoint.Value === null || totalSessionTime === null || targetDataTime === null) {
        return null;
      }

      return (
        '<div class="tooltip-card">' +
        '<div class="tooltip-card-header">' +
        '<h5 class="tooltip-card-title" style="color: ' + seriesColor + ';">' +
        (graphData.Series![seriesIndex].Name ?? 'No Name') + '<br>' + '(' + dateToDisplay + ')' +
        '</h5>' +
        '</div>' +
        '<div class="tooltip-card-body">' +
        '<div class="tooltip-item">' +
        '<span class="tooltip-label">Total Session Time:</span>' +
        '<span class="tooltip-value">' + totalSessionTime + '</span>' +
        '</div>' +
        '<div class="tooltip-item">' +
        '<span class="tooltip-label">Target Data Time:</span>' +
        '<span class="tooltip-value">' + targetDataTime + '</span>' +
        '</div>' +
        '<div class="tooltip-item">' +
        '<span class="tooltip-label">Percentage Calculated:</span>' +
        '<span class="tooltip-value">' + dataPoint.Value + '%</span>' +
        '</div>' +
        staffMembersHTML +
        '</div>' +
        '</div>'
      );
    };
  }

  // this is a generic tooltip for all graphs with simple data points
  createCustomTooltipSimple(graphData: GraphDataModel, staffMembers: DataTakerStaffDataModel[] = []) {
    const self = this;
    return function({ series, seriesIndex, dataPointIndex, w }: { 
      series: any[], 
      seriesIndex: number, 
      dataPointIndex: number, 
      w: any 
    }) {
      const seriesName = graphData.Series![seriesIndex].Name ?? 'No Name';
      const originalDataPointIndex = self.getDataPointIndexForTooltip(seriesName, dataPointIndex);
      
      // If this is a break point (mapped to -1), don't show tooltip
      if (originalDataPointIndex === -1) {
        return null;
      }
      
      const dataPoint = graphData.Series![seriesIndex].DataPoints![originalDataPointIndex];
      const dateToDisplay = new Date(dataPoint.Date ?? new Date().getTime()).toDateString();
      const seriesColor = w.globals.colors[seriesIndex%w.globals.colors.length] ?? '#008FFB';

      // create string of staff members names separated by commas
      const staffIDs = dataPoint.StaffIDs;
      const staffsForDataPoint = staffMembers.filter((staffMember) => staffIDs?.includes(staffMember.StaffID!));
      const staffMembersToDisplay = staffsForDataPoint.map((staffMember) => staffMember.FullName || 'No User' ).join(', ');
      const finalStaffDisplay = staffMembersToDisplay || 'No User';
      const staffMembersHTML = '<div class="tooltip-item">' +
        '<span class="tooltip-label">Staff Members:</span>' +
        '<span class="tooltip-value">' + finalStaffDisplay + '</span>' +
        '</div>';
      const textValueHTML = dataPoint.TextValue
        ? '<div class="tooltip-item">' +
          '<span class="tooltip-label">Details:</span>' +
          '<span class="tooltip-value">' + dataPoint.TextValue + '</span>' +
          '</div>'
        : '';
     
      if (dataPoint.Value === null) {
        return null;
      }

      return (
        '<div class="tooltip-card">' +
        '<div class="tooltip-card-header">' + 
        '<h5 class="tooltip-card-title" style="color: ' + seriesColor + ';">' +
        (graphData.Series![seriesIndex].Name ?? 'No Name') + '<br>' + '(' + dateToDisplay + ')' +
        '</h5>' +
        '</div>' +
        '<div class="tooltip-card-body">' +
        '<div class="tooltip-item">' +
        '<span class="tooltip-label">Value:</span>' +
        '<span class="tooltip-value">' + dataPoint.Value + '</span>' +
        '</div>' +
        textValueHTML +
        staffMembersHTML +
        '</div>' +
        '</div>'
      );
    }
  }

  createCustomTooltipTextValue(
    graphData: GraphDataModel,
    staffMembers: DataTakerStaffDataModel[] = [],
    valueLabel: string = 'Value',
    detailsLabel: string = 'Details'
  ) {
    const self = this;
    const escapeHtml = (value: any) => String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

    return function({ series, seriesIndex, dataPointIndex, w }: {
      series: any[],
      seriesIndex: number,
      dataPointIndex: number,
      w: any
    }) {
      const seriesName = graphData.Series![seriesIndex].Name ?? 'No Name';
      const originalDataPointIndex = self.getDataPointIndexForTooltip(seriesName, dataPointIndex);

      if (originalDataPointIndex === -1) {
        return null;
      }

      const dataPoint = graphData.Series![seriesIndex].DataPoints![originalDataPointIndex];
      if (dataPoint.Value === null) {
        return null;
      }

      const dateToDisplay = new Date(dataPoint.Date ?? new Date().getTime()).toDateString();
      const seriesColor = w.globals.colors[seriesIndex % w.globals.colors.length] ?? '#008FFB';
      const staffIDs = dataPoint.StaffIDs;
      const staffsForDataPoint = staffMembers.filter((staffMember) => staffIDs?.includes(staffMember.StaffID!));
      const staffMembersToDisplay = staffsForDataPoint.map((staffMember) => staffMember.FullName || 'No User').join(', ') || 'No User';
      const textLines = (dataPoint.TextValue ?? '')
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter((line) => line.length > 0);
      const textValueHTML = textLines.length > 0
        ? '<div class="tooltip-item">' +
          '<span class="tooltip-label">' + escapeHtml(detailsLabel) + ':</span>' +
          '<span class="tooltip-value">' +
          textLines.map((line) => '<div>' + escapeHtml(line) + '</div>').join('') +
          '</span>' +
          '</div>'
        : '';

      return (
        '<div class="tooltip-card">' +
        '<div class="tooltip-card-header">' +
        '<h5 class="tooltip-card-title" style="color: ' + seriesColor + ';">' +
        escapeHtml(seriesName) + '<br>' + '(' + escapeHtml(dateToDisplay) + ')' +
        '</h5>' +
        '</div>' +
        '<div class="tooltip-card-body">' +
        '<div class="tooltip-item">' +
        '<span class="tooltip-label">' + escapeHtml(valueLabel) + ':</span>' +
        '<span class="tooltip-value">' + escapeHtml(dataPoint.Value) + '</span>' +
        '</div>' +
        textValueHTML +
        '<div class="tooltip-item">' +
        '<span class="tooltip-label">Staff Members:</span>' +
        '<span class="tooltip-value">' + escapeHtml(staffMembersToDisplay) + '</span>' +
        '</div>' +
        '</div>' +
        '</div>'
      );
    }
  }

  createCustomTooltipMasteredTargets(graphData: GraphDataModel) {
    const self = this;
    return function({ series, seriesIndex, dataPointIndex, w }: { 
      series: any[], 
      seriesIndex: number, 
      dataPointIndex: number, 
      w: any 
    }) {
      const seriesName = graphData.Series![seriesIndex].Name ?? 'No Name';
      const originalDataPointIndex = self.getDataPointIndexForTooltip(seriesName, dataPointIndex);
      
      // If this is a break point (mapped to -1), don't show tooltip
      if (originalDataPointIndex === -1) {
        return null;
      }
      
      const dataPoint = graphData.Series![seriesIndex].DataPoints![originalDataPointIndex];
      const dateToDisplay = new Date(dataPoint.Date ?? new Date().getTime()).toDateString();
      const seriesColor = w.globals.colors[seriesIndex%w.globals.colors.length] ?? '#008FFB';

      if (dataPoint.RawValue1 === null || dataPoint.TextValue === null || dataPoint.RawValue1 === 0 || dataPoint.TextValue === '') {
        return null;
      }

      return (
        '<div class="tooltip-card">' +
        '<div class="tooltip-card-header">' +
        '<h5 class="tooltip-card-title" style="color: ' + seriesColor + ';">' +
        (graphData.Series![seriesIndex].Name ?? 'No Name') + '<br>' + '(' + dateToDisplay + ')' +
        '</h5>' +
        '</div>' +
        '<div class="tooltip-card-body">' +
        '<div class="tooltip-item">' +
        '<span class="tooltip-label">Mastered Targets Count:</span>' +
        '<span class="tooltip-value">' + dataPoint.RawValue1 + '</span>' +
        '</div>' +
        '<div class="tooltip-item">' +
        '<span class="tooltip-label">Mastered Target(s):</span>' +
        '<span class="tooltip-value">' + dataPoint.TextValue + '</span>' +
        '</div>' +
        '</div>' +
        '</div>'
      );
    };
  }

  createCustomTooltipPrompt(graphData: GraphDataModel, staffMembers: DataTakerStaffDataModel[] = [], fixedSeriesIndex?: number) {
    const self = this;
    return function({ series, seriesIndex, dataPointIndex, w }: { 
      series: any[], 
      seriesIndex: number, 
      dataPointIndex: number, 
      w: any 
    }) {
      // In synced charts each chart has one series, so seriesIndex is always 0; use fixedSeriesIndex when provided
      const effectiveSeriesIndex = fixedSeriesIndex !== undefined ? fixedSeriesIndex : seriesIndex;
      const seriesName = graphData.Series![effectiveSeriesIndex].Name ?? 'No Name';
      const originalDataPointIndex = self.getDataPointIndexForTooltip(seriesName, dataPointIndex);
      
      // If this is a break point (mapped to -1), don't show tooltip
      if (originalDataPointIndex === -1) {
        return null;
      }
      
      const dataPoint = graphData.Series![effectiveSeriesIndex].DataPoints![originalDataPointIndex];
      const totalPrompts = dataPoint.RawValue2;
      const targetCorrectPrompts = dataPoint.RawValue1;
      // create string of staff members names separated by commas
      const staffIDs = dataPoint.StaffIDs;
      const staffsForDataPoint = staffMembers.filter((staffMember) => staffIDs?.includes(staffMember.StaffID!));
      const staffMembersToDisplay = staffsForDataPoint.map((staffMember) => staffMember.FullName || 'No User').join(', ');
      const finalStaffDisplay = staffMembersToDisplay || 'No User';

      const dateToDisplay = new Date(dataPoint.Date ?? new Date().getTime()).toDateString();
      const seriesColor = w.globals.colors[effectiveSeriesIndex % w.globals.colors.length] ?? '#008FFB';

      if (dataPoint.Value === null || totalPrompts === null || targetCorrectPrompts === null) {
        return null;
      }

      const staffMembersHTML = '<div class="tooltip-item">' +
        '<span class="tooltip-label">Staff Members:</span>' +
        '<span class="tooltip-value">' + finalStaffDisplay + '</span>' +
        '</div>';

      return (
        '<div class="tooltip-card">' +
        '<div class="tooltip-card-header">' +
        '<h5 class="tooltip-card-title" style="color: ' + seriesColor + ';">' +
        (graphData.Series![effectiveSeriesIndex].Name ?? 'No Name') + '<br>' + '(' + dateToDisplay + ')' +
        '</h5>' +
        '</div>' +
        '<div class="tooltip-card-body">' +
        '<div class="tooltip-item">' +
        '<span class="tooltip-label">Total Prompts:</span>' +
        '<span class="tooltip-value">' + totalPrompts + '</span>' +
        '</div>' +
        '<div class="tooltip-item">' +
        '<span class="tooltip-label">Target Correct Prompts:</span>' +
        '<span class="tooltip-value">' + targetCorrectPrompts + '</span>' +
        '</div>' +
        '<div class="tooltip-item">' +
        '<span class="tooltip-label">Percentage Calculated:</span>' +
        '<span class="tooltip-value">' + dataPoint.Value + '%</span>' +
        '</div>' +
        staffMembersHTML +
        '</div>' +
        '</div>'
      );
    };
  }

  createCustomTooltipTimeOfDay(graphData: GraphDataModel, staffMembers: DataTakerStaffDataModel[] = []) {
    const self = this;
    return function({ series, seriesIndex, dataPointIndex, w }: { 
      series: any[], 
      seriesIndex: number, 
      dataPointIndex: number, 
      w: any 
    }) {
      const seriesName = graphData.Series![seriesIndex].Name ?? 'No Name';
      const originalDataPointIndex = self.getDataPointIndexForTooltip(seriesName, dataPointIndex);
      
      // If this is a break point (mapped to -1), don't show tooltip
      if (originalDataPointIndex === -1) {
        return null;
      }
      
      const dataPoint = graphData.Series![seriesIndex].DataPoints![originalDataPointIndex];
      // the date should be in the format of "MMM dd yyyy - HH:MM:SS"
      const dateToDisplay = new Date(dataPoint.Date ?? new Date().getTime()).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });
      // time from the day start in seconds
      const timeFromDayStart = dataPoint.Value ?? 0;
      // time calculated from the day start
      const timeFromDayStartFormatted = Utils.formatTime(timeFromDayStart);
      const seriesColor = w.globals.colors[seriesIndex%w.globals.colors.length] ?? '#008FFB';

      // create string of staff members names separated by commas
      const staffIDs = dataPoint.StaffIDs;
      const staffsForDataPoint = staffMembers.filter((staffMember) => staffIDs?.includes(staffMember.StaffID!));
      const staffMembersToDisplay = staffsForDataPoint.map((staffMember) => staffMember.FullName || 'No User').join(', ');
      const finalStaffDisplay = staffMembersToDisplay || 'No User';
      const staffMembersHTML = '<div class="tooltip-item">' +
        '<span class="tooltip-label">Staff Members:</span>' +
        '<span class="tooltip-value">' + finalStaffDisplay + '</span>' +
        '</div>';
      const intensityHTML = dataPoint.TextValue
        ? '<div class="tooltip-item">' +
          '<span class="tooltip-label">Intensity:</span>' +
          '<span class="tooltip-value">' + dataPoint.TextValue + '</span>' +
          '</div>'
        : '';
      
      return '<div class="tooltip-card">' +
        '<div class="tooltip-card-header">' +
        '<h5 class="tooltip-card-title" style="color: ' + seriesColor + ';">' +
        (graphData.Series![seriesIndex].Name ?? 'No Name') +
        '</h5>' +
        '</div>' +
        '<div class="tooltip-card-body">' +
        '<div class="tooltip-item">' +
        '<span class="tooltip-label">Date:</span>' +
        '<span class="tooltip-value">' + dateToDisplay + '</span>' +
        '</div>' +
        intensityHTML +
        staffMembersHTML +
        // '<div class="tooltip-item">' +
        // '<span class="tooltip-label">Time from Day Start:</span>' +
        // '<span class="tooltip-value">' + timeFromDayStart + ' seconds' + '</span>' +
        // '</div>' +
        // '<div class="tooltip-item">' +
        // '<span class="tooltip-label">Time from Day Start (Formatted):</span>' +
        // '<span class="tooltip-value">' + timeFromDayStartFormatted + '</span>' +
        // '</div>' +
        '</div>' +
        '</div>'
      ;
    };
  }

  createCustomTooltipPercentageOnGoal(graphData: GraphDataModel, staffMembers: DataTakerStaffDataModel[] = []) {
    return function({ series, seriesIndex, dataPointIndex, w }: { 
      series: any[], 
      seriesIndex: number, 
      dataPointIndex: number, 
      w: any 
    }) {
      const dataPoint = graphData.Series![seriesIndex].DataPoints![dataPointIndex];
      // time in format of mm:ss
      const targetGoalTime = dataPoint.RawValue1 ? Utils.formatTime(dataPoint.RawValue1) : 'N/A';
      const targetDataTime = dataPoint.RawValue2 ? Utils.formatTime(dataPoint.RawValue2) : 'N/A';

      // create string of staff members names separated by commas
      const staffIDs = dataPoint.StaffIDs;
      const staffsForDataPoint = staffMembers.filter((staffMember) => staffIDs?.includes(staffMember.StaffID!));
      const staffMembersToDisplay = staffsForDataPoint.map((staffMember) => staffMember.FullName || 'No User').join(', ');
      const finalStaffDisplay = staffMembersToDisplay || 'No User';
      const staffMembersHTML = '<div class="tooltip-item">' +
        '<span class="tooltip-label">Staff Members:</span>' +
        '<span class="tooltip-value">' + finalStaffDisplay + '</span>' +
        '</div>';

      const dateToDisplay = new Date(dataPoint.Date ?? new Date().getTime()).toDateString();
      const seriesColor = w.globals.colors[seriesIndex%w.globals.colors.length] ?? '#008FFB';

      if (dataPoint.Value === null || targetGoalTime === null || targetDataTime === null) {
        return null;
      }

      return (
        '<div class="tooltip-card">' +
        '<div class="tooltip-card-header">' +
        '<h5 class="tooltip-card-title" style="color: ' + seriesColor + ';">' +
        (graphData.Series![seriesIndex].Name ?? 'No Name') + '<br>' + '(' + dateToDisplay + ')' +
        '</h5>' +
        '</div>' +
        '<div class="tooltip-card-body">' +
        '<div class="tooltip-item">' +
        '<span class="tooltip-label">Target Goal Time:</span>' +
        '<span class="tooltip-value">' + targetGoalTime + '</span>' +
        '</div>' +
        '<div class="tooltip-item">' +
        '<span class="tooltip-label">Target Data Time:</span>' +
        '<span class="tooltip-value">' + targetDataTime + '</span>' +
        '</div>' +
        '<div class="tooltip-item">' +
        '<span class="tooltip-label">Percentage Calculated:</span>' +
        '<span class="tooltip-value">' + dataPoint.Value + '%</span>' +
        '</div>' +
        staffMembersHTML +
        '</div>' +
        '</div>'
      );
    };
  }

  xaxisAnnotationsFromGraphDataForSyncedTargets(graphData: GraphDataModel, series: GraphSeriesModel) {
    var annotationsForXAxis = graphData?.Annotations?.filter((annotation) => annotation.GraphAnnotationType === GraphAnnotationType.XAxis || annotation.GraphAnnotationType === GraphAnnotationType.XAxisRange);
        
    // we need to filter the annotations to only include the ones for the target being graphed
    if (series.PatientTargetID) {
      annotationsForXAxis = annotationsForXAxis?.filter((annotation) => 
        annotation.PatientTargetIDs?.includes(series.PatientTargetID!) || 
      ((annotation.PatientProgramIDs?.length ?? 0) > 0)
      );
    }

    const declutterCandidates = this.declutterAnnotations()
      ? this.resolveDeclutterAnnotationCandidates(graphData)
      : [];

    return annotationsForXAxis
      ?.filter((annotation) => this.isAnnotationDisplayed(annotation))
      .map((annotation) => {
        const color = this.resolveAnnotationColor(annotation, '#008FFB', declutterCandidates);
        const placement = this.resolveXAxisAnnotationLabelPlacement(annotation);
        if (annotation.GraphAnnotationType === GraphAnnotationType.XAxis) {
          return {
            id: this.annotationApexId(annotation),
            x: new Date(annotation.StartDate!).getTime(),
            strokeDashArray: 5,
            borderColor: color,
            strokeOpacity: this.resolveAnnotationLineStrokeOpacity(),
            borderWidth: this.resolveAnnotationLineBorderWidth(),
            label: {
              borderColor: color,
              style: { ...this.buildAnnotationLabelStyle(annotation, color), textAlign: 'left' },
              position: placement.position,
              offsetY: placement.offsetY,
              text: this.resolveAnnotationLabelText(annotation, annotation.Label ?? '') as any,
            },
          };
        }

        return {
          id: this.annotationApexId(annotation),
          x: new Date(annotation.StartDate!).getTime(),
          x2: new Date(annotation.EndDate!).getTime(),
          strokeDashArray: 5,
          borderColor: color,
          strokeOpacity: this.resolveAnnotationLineStrokeOpacity(),
          borderWidth: this.resolveAnnotationLineBorderWidth(),
          fillOpacity: 0.1,
          fillColor: color,
          label: {
            borderColor: color,
            style: { ...this.buildAnnotationLabelStyle(annotation, color), textAlign: 'left' },
            position: placement.position,
            offsetY: placement.offsetY,
            text: this.resolveAnnotationLabelText(annotation, annotation.Label ?? '') as any,
          },
        };
      }) ?? [];
  }

  yaxisAnnotationsFromGraphDataForSyncedTargets(graphData: GraphDataModel, series: GraphSeriesModel) {
    var annotationsForYAxis = graphData?.Annotations?.filter((annotation) => annotation.GraphAnnotationType === GraphAnnotationType.YAxis || annotation.GraphAnnotationType === GraphAnnotationType.YAxisRange);
    
    // we need to filter the annotations to only include the ones for the target being graphed
    if (series.PatientTargetID) {
      annotationsForYAxis = annotationsForYAxis?.filter((annotation) => 
        annotation.PatientTargetIDs?.includes(series.PatientTargetID!) || 
      ((annotation.PatientProgramIDs?.length ?? 0) > 0)
      );
    }

    const declutterCandidates = this.declutterAnnotations()
      ? this.resolveDeclutterAnnotationCandidates(graphData)
      : [];
    
    return annotationsForYAxis
      ?.filter((annotation) => this.isAnnotationDisplayed(annotation))
      .map((annotation) => {
        const color = this.resolveAnnotationColor(annotation, '#008FFB', declutterCandidates);
        const placement = this.resolveYAxisAnnotationLabelPlacement(annotation);
        if (annotation.GraphAnnotationType === GraphAnnotationType.YAxis) {
          return {
            id: this.annotationApexId(annotation),
            y: annotation.YValueStart,
            strokeDashArray: 5,
            borderColor: color,
            strokeOpacity: this.resolveAnnotationLineStrokeOpacity(),
            borderWidth: this.resolveAnnotationLineBorderWidth(),
            label: {
              borderColor: color,
              style: this.buildAnnotationLabelStyle(annotation, color),
              position: placement.position,
              offsetX: placement.offsetX,
              textAnchor: placement.textAnchor,
              text: this.resolveAnnotationLabelText(annotation, annotation.Label + ' - ' + annotation.YValueStart) as any,
            },
          };
        }

        return {
          id: this.annotationApexId(annotation),
          y: annotation.YValueStart,
          y2: annotation.YValueEnd,
          strokeDashArray: 5,
          borderColor: color,
          strokeOpacity: this.resolveAnnotationLineStrokeOpacity(),
          borderWidth: this.resolveAnnotationLineBorderWidth(),
          fillOpacity: 0.1,
          fillColor: color,
          label: {
            borderColor: color,
            style: this.buildAnnotationLabelStyle(annotation, color),
            position: placement.position,
            offsetX: placement.offsetX,
            textAnchor: placement.textAnchor,
            text: this.resolveAnnotationLabelText(annotation, annotation.Label + ' - ' + annotation.YValueStart + ' to ' + annotation.YValueEnd) as any,
          },
        };
      }) ?? [];
  }
  
  pointsAnnotationsFromGraphDataForSyncedTargets(graphData: GraphDataModel, series: GraphSeriesModel) {
    return [];
  }
  
  textsAnnotationsFromGraphDataForSyncedTargets(graphData: GraphDataModel, series: GraphSeriesModel) {
    return [];
  }

  /**
   * Formats annotation label text to use multiple lines if it exceeds 30 characters
   */
  protected formatAnnotationLabelText(text: string): string | string[] {
    if (text.length <= 30) {
      return text;
    }

    // Split the text into words and filter out empty strings
    const words = text.split(' ').filter(word => word.length > 0);
    const lines: string[] = [];
    let currentLine = '';

    for (const word of words) {
      // If adding this word would exceed 30 characters, start a new line
      if (currentLine.length + word.length + 1 > 30 && currentLine.length > 0) {
        lines.push(currentLine);
        currentLine = word;
      } else {
        if (currentLine.length === 0) {
          currentLine = word;
        } else {
          currentLine += ' ' + word;
        }
      }
    }

    // Add the last line if it has content
    if (currentLine.length > 0) {
      lines.push(currentLine);
    }

    // Return array of strings for multiline support in ApexCharts
    // Pad each line to exactly 30 characters with whitespace
    if (lines.length > 1) {
      return lines.map(line => line.padEnd(30, ' '));
    }
    
    return text;
  }

  /**
   * Creates an event handler that makes annotation labels more subtle
   */
  protected createAnnotationReorderEvent() {
    const onChartRendered = (chartContext: any, config: any) => {
      // Add CSS to make annotation labels more subtle
      const chartElement = chartContext.el;
      const style = document.createElement('style');
      style.textContent = `
               .apexcharts-annotation-label {
                 z-index: 1 !important;
                 background: rgba(255, 255, 255, 0.1) !important;
                 backdrop-filter: blur(2px) !important;
                 font-size: 14px !important;
                 font-family: monospace !important;
               }
               .apexcharts-annotation-text {
                 z-index: 1 !important;
                 opacity: 0.3 !important;
                 font-size: 14px !important;
                 font-family: monospace !important;
               }
               .apexcharts-annotations text {
                 z-index: 1 !important;
                 opacity: 0.3 !important;
                 font-size: 14px !important;
                 font-family: monospace !important;
               }
               .apexcharts-annotations .apexcharts-annotation-label {
                 z-index: 1 !important;
                 background: rgba(255, 255, 255, 0.1) !important;
                 backdrop-filter: blur(2px) !important;
                 font-size: 14px !important;
                 font-family: monospace !important;
               }
      `;
      chartElement.appendChild(style);

      const toolbar = chartElement.querySelector('.apexcharts-toolbar');
      if (toolbar && !toolbar.hasAttribute('data-declutter-export-handler')) {
        toolbar.setAttribute('data-declutter-export-handler', 'true');
        toolbar.addEventListener('click', (event: Event) => {
          const target = event.target as Element | null;
          const menuItem = target?.closest('.apexcharts-menu-item');
          const exportFormat = menuItem?.classList.contains('exportSVG')
            ? 'svg'
            : menuItem?.classList.contains('exportPNG')
              ? 'png'
              : menuItem?.classList.contains('exportCSV')
                ? 'csv'
                : null;

          if (!exportFormat || !this.graphExportService || !this.shouldDisplayDeclutterLegend()) {
            return;
          }

          const graphData = this.getGraphDataForAnnotations();
          if (!graphData) {
            return;
          }

          const legendItems = this.isGrouped()
            ? this.getDeclutterLegendItems(graphData)
            : this.getDeclutterLegendItemsForChart(graphData, chartContext.w?.config);
          if (!legendItems.length) {
            return;
          }

          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
          void this.graphExportService.downloadApexChart(
            chartContext,
            exportFormat,
            legendItems
          );
        }, true);
      }
    };
    return { mounted: onChartRendered, updated: onChartRendered };
  }

  /**
   * Processes series data to insert null values at break points where annotations have shouldBreakSeries = true
   * This creates visual breaks in the line chart at those annotation points
   * Returns both the processed data and a mapping from processed index to original index
   */
  processSeriesDataWithBreaks(series: GraphSeriesModel, graphData: GraphDataModel): { 
    data: [number, number | null][], 
    indexMapping: number[] 
  } {
    if (!series.DataPoints || !graphData.Annotations) {
      const data = series.DataPoints!.map((data) => [
        data.Date ? new Date(data.Date).getTime() : new Date().getTime(),
        data.Value,
      ] as [number, number | null]);
      const indexMapping = Array.from({ length: data.length }, (_, i) => i);
      this.seriesIndexMappings.set(series.Name || 'unknown', indexMapping);
      return { data, indexMapping };
    }

    // Convert data points to array with timestamps
    const dataPoints = series.DataPoints!.map((data) => ({
      timestamp: data.Date ? new Date(data.Date).getTime() : new Date().getTime(),
      value: data.Value,
    }));

    // Sort data points by timestamp
    dataPoints.sort((a, b) => a.timestamp - b.timestamp);
    const minTimestamp = dataPoints[0]?.timestamp ?? 0;
    const maxTimestamp = dataPoints[dataPoints.length - 1]?.timestamp ?? 0;

    // Get x-axis annotations that should break the series for this specific series.
    // Only include break annotations within the graph data range so they don't force the view to extend.
    const breakAnnotations = graphData.Annotations.filter(annotation => {
      if (annotation.ShouldBreakSeries !== true ||
          annotation.GraphAnnotationType !== GraphAnnotationType.XAxis ||
          !this.isAnnotationDisplayed(annotation) ||
          !annotation.StartDate) {
        return false;
      }
      const annotationTime = new Date(annotation.StartDate).getTime();
      return annotationTime >= minTimestamp && annotationTime <= maxTimestamp;
    });

    if (breakAnnotations.length === 0) {
      // No break annotations in range, return original data
      const data = series.DataPoints!.map((data) => [
        data.Date ? new Date(data.Date).getTime() : new Date().getTime(),
        data.Value,
      ] as [number, number | null]);
      const indexMapping = Array.from({ length: data.length }, (_, i) => i);
      this.seriesIndexMappings.set(series.Name || 'unknown', indexMapping);
      return { data, indexMapping };
    }

    // Get break timestamps (all in range by construction)
    const breakTimestamps = breakAnnotations.map(annotation => 
      new Date(annotation.StartDate!).getTime()
    ).sort((a, b) => a - b);

    const result: [number, number | null][] = [];
    const indexMapping: number[] = [];
    let breakIndex = 0;

    for (let i = 0; i < dataPoints.length; i++) {
      const currentPoint = dataPoints[i];
      
      // Check if we need to insert a break before this point
      while (breakIndex < breakTimestamps.length && 
             breakTimestamps[breakIndex] < currentPoint.timestamp) {
        // Insert null value at the break timestamp
        result.push([breakTimestamps[breakIndex], null]);
        indexMapping.push(-1); // -1 indicates this is a break point (no original data point)
        breakIndex++;
      }
      
      // Add the current data point
      result.push([currentPoint.timestamp, currentPoint.value ?? null]);
      indexMapping.push(i); // Map to original data point index
    }

    // Add any remaining breaks after the last data point
    while (breakIndex < breakTimestamps.length) {
      result.push([breakTimestamps[breakIndex], null]);
      indexMapping.push(-1); // -1 indicates this is a break point (no original data point)
      breakIndex++;
    }

    this.seriesIndexMappings.set(series.Name || 'unknown', indexMapping);
    return { data: result, indexMapping };
  }

  /**
   * Gets the correct data point index for tooltips, accounting for breaks in series
   */
  protected getDataPointIndexForTooltip(seriesName: string, processedIndex: number): number {
    const mapping = this.seriesIndexMappings.get(seriesName);
    if (!mapping || processedIndex >= mapping.length) {
      return processedIndex; // Fallback to original index
    }
    return mapping[processedIndex];
  }

  heightForChart(isPrintingPdf: boolean, isPrintingPng: boolean): number {
    if (isPrintingPdf) {
      return 390;
    }
    if (isPrintingPng) {
      return 390;
    }
    return 350;
  }

  widthForChart(isPrintingPdf: boolean, isPrintingPng: boolean): number | undefined {
    if (isPrintingPdf) {
      return 390 * 2.5;
    }
    if (isPrintingPng) {
      return undefined;
    }
    return undefined;
  }
}
